import React, { createContext, useContext, useState, useEffect } from "react";

const translations = {
  en: {
    // Common
    dashboard: "Dashboard",
    transactions: "Transactions",
    deposits: "Deposits",
    withdrawals: "Withdrawals",
    merchants: "Merchants",
    providers: "Providers",
    routing: "Routing",
    webhooks: "Webhooks",
    settlements: "Settlements",
    settings: "Settings",
    engines: "Engines",
    resellers: "Resellers",
    servers: "Servers",
    allTransactions: "All Transactions",
    staff: "Staff",
    staffMembers: "Staff Members",
    staffMembersCount: "staff members",
    staffMember: "Staff Member",
    createStaffMember: "Create Staff Member",
    noStaffMembersFound: "No staff members found",
    noRolesAssigned: "No roles assigned",
    changeStaffStatus: "Change Staff Status",
    deleteStaffMember: "Delete Staff Member",
    areYouSureYouWantTo: "Are you sure you want to",
    actions: "Actions",
    roles: "Roles",
    description: "Description",

    // Actions
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    add: "Add",
    create: "Create",
    update: "Update",
    view: "View",
    search: "Search",
    filter: "Filter",
    export: "Export",
    import: "Import",
    refresh: "Refresh",
    reset: "Reset",
    submit: "Submit",
    confirm: "Confirm",
    close: "Close",
    back: "Back",
    next: "Next",
    previous: "Previous",
    loading: "Loading...",
    saving: "Saving...",
    creating: "Creating...",
    processing: "Processing...",

    // Status
    active: "Active",
    inactive: "Inactive",
    pending: "Pending",
    success: "Success",
    failed: "Failed",
    expired: "Expired",
    cancelled: "Cancelled",
    declined: "Declined",
    suspended: "Suspended",
    terminated: "Terminated",
    completed: "Completed",
    maintenance: "Maintenance",
    degraded: "Degraded",
    ewallet: "E-Wallet",
    card: "Card",
    qr: "QR",
    bank: "Bank",

    // Time
    today: "Today",
    thisDate: "Today",
    thisWeek: "This Week",
    thisMonth: "This Month",
    thisYear: "This Year",
    customRange: "Custom Range",
    selectRange: "Select Range",

    // Navigation
    logout: "Logout",
    profile: "Profile",
    notifications: "Notifications",
    markAllRead: "Mark all read",
    viewAllNotifications: "View all notifications",

    // Dashboard
    paymentOrchestration: "Payment orchestration overview",
    totalDeposits: "Total Deposits",
    totalWithdrawals: "Total Withdrawals",
    successRate: "Success Rate",
    activeMerchants: "Active Merchants",
    quickActions: "Quick Actions",
    addMerchant: "Add Merchant",
    onboardNewMerchant: "Onboard new merchant",
    configureProvider: "Configure Provider",
    addPaymentProvider: "Add payment provider",
    routingRules: "Routing Rules",
    configureRouting: "Configure routing",
    pendingTransactions: "Pending Transactions",
    requiresAttention: "Requires attention",
    volumeOverview: "Volume Overview",
    last7Days: "Last 7 days transaction volume",
    recentTransactions: "Recent Transactions",
    viewAll: "View All",
    totalVolume: "Total Volume",
    volumeOverviewTitle: "Volume Overview",
    transactionVolume: "Transaction Volume",
    allStatus: "All Status",

    // Transactions
    transactionsFound: "transactions found",
    transactionId: "Transaction ID",
    merchantRef: "Merchant Ref",
    type: "Type",
    amount: "Amount",
    status: "Status",
    date: "Date",
    provider: "Provider",
    deposit: "Deposit",
    withdrawal: "Withdrawal",
    showing: "Showing",
    to: "to",
    of: "of",
    page: "Page",

    // Merchants
    merchantsRegistered: "merchants registered",
    searchMerchants: "Search merchants...",
    merchant: "Merchant",
    code: "Code",
    parent: "Parent",
    features: "Features",
    fee: "Fee",
    settlement: "Settlement",
    direct: "Direct",
    viewDetails: "View Details",
    viewApiKeys: "View API Keys",
    suspend: "Suspend",
    activate: "Activate",
    deleteMerchant: "Delete Merchant",
    deleteConfirmation: "Are you sure you want to delete",
    cannotBeUndone: "This action cannot be undone.",
    suspendMerchantTitle: "Suspend Merchant",
    activateMerchantTitle: "Activate Merchant",
    suspendMerchantConfirm: "Are you sure you want to suspend",
    activateMerchantConfirm: "Are you sure you want to activate",
    willNotProcessTransactions: "They will not be able to process transactions.",
    noMerchantsFound: "No merchants found",

    // Providers
    paymentProviders: "Payment Providers",
    activeProviders: "active providers",
    searchProviders: "Search providers...",
    addProvider: "Add Provider",
    healthScore: "Health Score",
    response: "Response",
    priority: "Priority",
    disable: "Disable",
    enable: "Enable",
    deleteProvider: "Delete Provider",
    affectRouting: "This will affect routing rules.",

    // Routing
    routingRulesTitle: "Routing Rules",
    configureProviderSelection: "Configure provider selection and failover logic",
    addRule: "Add Rule",
    howRoutingWorks: "How Routing Works",
    routingDescription: "Rules are evaluated in priority order. The first matching rule determines which providers handle the transaction.",
    noRoutingRules: "No routing rules configured",
    viewTransactionRoutingConfig: "View transaction routing configuration",
    failover: "Failover",
    amountRange: "Amount Range",
    createFirstRule: "Create your first routing rule to start directing transactions",
    defaultRule: "Default Rule",
    ruleName: "Rule Name",
    transactionType: "Transaction Type",
    paymentMethod: "Payment Method",
    minAmount: "Min Amount",
    maxAmount: "Max Amount",
    specificMerchant: "Specific Merchant (optional)",
    allMerchants: "All Merchants",
    routingMode: "Routing Mode",
    providerSequence: "Provider Sequence (click to add/remove)",
    clickProvidersOrder: "Click providers in the order you want them used. Numbers show priority.",
    enableFailover: "Enable Failover",
    autoTryNextProvider: "Automatically try next provider on failure",
    maxFailoverAttempts: "Max Failover Attempts",
    allTypes: "All Types",
    depositsOnly: "Deposits Only",
    withdrawalsOnly: "Withdrawals Only",
    allMethods: "All Methods",
    priorityMode: "Priority (use first available)",
    roundRobin: "Round Robin (distribute evenly)",
    weighted: "Weighted (by provider weight)",
    leastLoad: "Least Load (by current volume)",
    editRoutingRule: "Edit Routing Rule",
    createRoutingRule: "Create Routing Rule",
    deleteRoutingRule: "Delete Routing Rule",
    affectTransactionRouting: "This may affect transaction routing.",

    // Webhook Logs
    webhookLogs: "Webhook Logs",
    webhookEvents: "webhook events",
    webhookId: "Webhook ID",
    direction: "Direction",
    source: "Source",
    event: "Event",
    inbound: "Inbound",
    outbound: "Outbound",
    allDirection: "All Direction",
    allStatus: "All Status",
    received: "Received",
    validated: "Validated",
    processed: "Processed",
    invalidSignature: "Invalid Signature",
    invalid_signature: "Invalid Signature",
    webhookLog: "Webhook Log",
    webhookDetails: "Webhook Details",
    requestHeaders: "Request Headers",
    requestPayload: "Request Payload",
    responseStatus: "Response",
    noWebhookLogs: "No webhook logs found",
    duplicate: "Duplicate",
    failedToLoadWebhookLogs: "Failed to load webhook logs",
    noResponseBody: "No response body",

    // Settlements
    settlementTitle: "Settlements",
    merchantPayoutManagement: "Merchant payout management",
    createSettlement: "Create Settlement",
    searchSettlements: "Search settlements...",
    settlementId: "Settlement ID",
    period: "Period",
    netAmount: "Net Amount",
    process: "Process",
    markCompleted: "Mark Completed",
    exportReport: "Export Report",
    periodStart: "Period Start",
    periodEnd: "Period End",
    totalFees: "Total Fees",
    totalSettled: "Total Settled",
    noSettlements: "No settlements found",

    // Settings
    settingsTitle: "Settings",
    platformConfig: "Platform configuration and preferences",
    saveChanges: "Save Changes",
    general: "General",
    security: "Security",
    callbacks: "Callbacks",
    qrSettings: "QR Settings",
    notificationsSettings: "Notifications",
    generalSettings: "General Settings",
    basicPlatformConfig: "Basic platform configuration",
    platformName: "Platform Name",
    timezone: "Timezone",
    defaultCurrency: "Default Currency",
    securitySettings: "Security Settings",
    apiSecurityConfig: "API security and authentication configuration",
    requireIpWhitelist: "Require IP Whitelist",
    acceptApiCallsFromWhitelistedIps: "Only accept API calls from whitelisted IPs",
    hmacAlgorithm: "HMAC Algorithm",
    signatureHeaderName: "Signature Header Name",
    timestampTolerance: "Timestamp Tolerance (seconds)",
    rejectOldRequests: "Reject requests with timestamps older than this value",
    callbackSettings: "Callback Settings",
    configureCallback: "Configure merchant callback behavior",
    enableCallbackRetry: "Enable Callback Retry",
    autoRetryFailedCallbacks: "Automatically retry failed callbacks",
    maxRetryAttempts: "Max Retry Attempts",
    retryDelaySchedule: "Retry Delay Schedule (seconds)",
    callbackTimeout: "Callback Timeout (seconds)",
    qrPaymentSettings: "QR Payment Settings",
    promptPayConfig: "PromptPay QR code configuration",
    defaultQrExpiry: "Default QR Expiry (minutes)",
    minimumAmount: "Minimum Amount (THB)",
    maximumAmount: "Maximum Amount (THB)",
    supportedBanks: "Supported Banks",
    enableInProviderSettings: "Enable in Provider settings",
    notificationSettings: "Notification Settings",
    configureAlerts: "Configure alerts and notifications",
    emailNotifications: "Email Notifications",
    sendAlertsViaEmail: "Send alerts via email",
    slackNotifications: "Slack Notifications",
    sendAlertsToSlack: "Send alerts to Slack channel",
    alertThreshold: "Alert Threshold (THB)",
    sendAlertAboveAmount: "Send alert for transactions above this amount",
    alertTypes: "Alert Types",
    highValueTransactions: "High-value transactions",
    providerFailures: "Provider failures / degradation",
    failedCallbackRetries: "Failed callback retries",
    suspiciousActivity: "Suspicious activity detection",

    // Engine Settings
    feesAndCommission: "Fees & Commission",
    feesAndCommissionDesc: "Commission rates and fee information",
    givenBy: "Given by {type}: {name}",
    yourCommissionRates: "Your Commission Rates",
    yourCommissionRatesDesc: "These are the rates assigned to you",
    depositCommission: "Deposit Commission",
    withdrawalCommission: "Withdrawal Commission",
    percentageFee: "Percentage Fee",
    depositCommissionPercent: "Deposit Commission (%)",
    withdrawalCommissionPercent: "Withdrawal Commission (%)",
    resellerProfile: "Reseller Profile",
    manageProfile: "Manage your profile settings",
    apiCredentialsTitle: "API Credentials",
    yourApiKeysIntegration: "Your API keys for integration",
    regenerateApiKeysTitle: "Regenerate Keys",
    regenerateApiKeysDesc: "This will invalidate your current API keys immediately.",
    regenerateApiKeysConfirm: "Are you sure you want to regenerate your API keys? This action cannot be undone.",

    // Engines
    enginePartners: "engine partners",
    searchEngines: "Search engines...",
    createEngine: "Create Engine",
    engine: "Engine",
    volume: "Volume",
    created: "Created",
    suspendEngine: "Suspend Engine",
    activateEngine: "Activate Engine",
    allResellersAffected: "All resellers under them will be affected.",
    deleteEngine: "Delete Engine",
    editEngine: "Edit Engine",
    createNewEngine: "Create New Engine",
    updateEngineDetails: "Update engine details",
    setUpNewEnginePartner: "Set up a new engine partner",
    generalTab: "General",
    feesLimitsTab: "Fees & Limits",
    permissionsTab: "Permissions",
    apiAccessTab: "API Access",
    generalInformation: "General Information",
    businessNameRequired: "Business Name *",
    engineCode: "Engine Code",
    loginEmailRequired: "Login Email *",
    newPasswordOptional: "New Password (optional)",
    passwordRequired: "Password *",
    leaveBlankToKeepCurrent: "Leave blank to keep current",
    enterPassword: "Enter password",
    status: "Status",
    active: "Active",
    inactive: "Inactive",
    suspended: "Suspended",
    contactName: "Contact Name",
    contactPhone: "Contact Phone",
    supportEmail: "Support Email",
    notes: "Notes",
    configureFeeStructureLimits: "Configure fee structure and operational limits",
    depositFeeThb: "Deposit Fee (฿)",
    withdrawalFeeThb: "Withdrawal Fee (฿)",
    percentageFeePercent: "Percentage Fee (%)",
    commissionRatePercent: "Commission Rate (%)",
    maxResellers: "Max Resellers",
    maxMerchants: "Max Merchants",
    allowedPaymentProviders: "Allowed Payment Providers",
    controlEngineActions: "Control what actions this engine can perform",
    canCreateResellers: "Can Create Resellers",
    allowCreatingNewResellerAccounts: "Allow creating new reseller accounts",
    canCreateMerchants: "Can Create Merchants",
    canCreateMerchantsDirect: "Can Create Merchants (Direct)",
    allowCreatingMerchantAccountsDirectly: "Allow creating merchant accounts directly",
    canEditFeeStructure: "Can Edit Fee Structure",
    allowModifyingFeeSettings: "Allow modifying fee settings",
    canViewWebhookLogs: "Can View Webhook Logs",
    accessToRawWebhookData: "Access to raw webhook data",
    canRegenerateApiKeys: "Can Regenerate API Keys",
    allowRegeneratingApiCredentials: "Allow regenerating API credentials",
    apiCredentials: "API Credentials",
    shareCredentialsSecurely: "Share these credentials securely with the engine partner",
    keepCredentialsSecure: "Keep these credentials secure. They provide full API access.",
    updateEngine: "Update Engine",
    saving: "Saving...",
    pleaseFillAllRequiredFields: "Please fill in all required fields",
    engineCodePlaceholder: "ENG001",
    businessNamePlaceholder: "Gaming Partners Ltd",
    loginEmailPlaceholder: "admin@engine.com",

    // Resellers
    resellerPartners: "engine partners registered",
    searchResellers: "Search resellers...",
    createReseller: "Create Reseller",
    reseller: "Reseller",
    monthlyVolume: "Monthly Volume",
    suspendReseller: "Suspend Reseller",
    activateReseller: "Activate Reseller",
    merchantsWillBeAffected: "They and their merchants will not be able to process transactions.",
    deleteReseller: "Delete Reseller",
    merchantsBecomeOrphaned: "All merchants under this reseller will become orphaned.",
    editReseller: "Edit Reseller",
    createNewReseller: "Create New Reseller",
    updateResellerDetails: "Update reseller details and permissions",
    setUpNewResellerPartner: "Set up a new reseller partner",
    resellerProfileAndContact: "Reseller profile and contact details",
    resellerCode: "Reseller Code",
    parentType: "Parent Type",
    whoManagesThisReseller: "Who manages this reseller",
    emailCannotBeChangedAfterCreation: "Email cannot be changed after creation",
    newPasswordLeaveBlank: "New Password (leave blank to keep current)",
    configureResellerFeeStructure: "Configure reseller fee structure and limits",
    depositFeeThb: "Deposit Fee (฿)",
    withdrawalFeeThb: "Withdrawal Fee (฿)",
    percentageFeePercent: "Percentage Fee (%)",
    commissionRatePercent: "Commission Rate (%)",
    maxMerchantsLabel: "Max Merchants",
    maximumMerchantsResellerCanCreate: "Maximum merchants this reseller can create",
    resellerCommissionOnTransactions: "Reseller's commission on transactions",
    thisCommissionRateSetBy: "This commission rate was set by {name} and cannot be changed.",
    allowedProviders: "Allowed Providers",
    selectWhichPaymentProviders: "Select which payment providers this reseller can use",
    routingOverride: "Routing Override",
    configureRoutingBehavior: "Configure routing behavior for this reseller",
    failoverEnabled: "Failover Enabled",
    automaticallyTryNextProvider: "Automatically try next provider on failure",
    priorityProvidersInOrder: "Priority Providers (in order)",
    featurePermissions: "Feature Permissions",
    controlWhatActionsReseller: "Control what actions this reseller can perform",
    visibleDashboardPages: "Visible Dashboard Pages",
    controlWhichPagesReseller: "Control which pages the reseller can access",
    resellerApiKeysForIntegration: "Reseller API keys for integration",
    apiKeysAutoGenerated: "API keys are auto-generated. Share these securely with the reseller.",
    updateReseller: "Update Reseller",
    createResellerButton: "Create Reseller",

    // Global Transactions
    globalTransactions: "Global Transactions",
    transactionsAcrossEntities: "transactions across all entities",
    filters: "Filters",
    clearAll: "Clear All",
    searchIdRefMerchant: "Search ID, ref, merchant...",
    allEngines: "All Engines",
    allResellers: "All Resellers",
    allProviders: "All Providers",
    allMerchants: "All Merchants",
    fromDate: "From Date",
    toDate: "To Date",
    activeFilters: "active",
    errorLoadingTransactions: "Error loading transactions",
    pleaseCheckConnection: "Please check your connection and try again.",

    // Servers
    serverInfrastructure: "Server Infrastructure",
    realTimeMonitoring: "Real-time monitoring and health status",
    last1h: "Last 1h",
    last6h: "Last 6h",
    last24h: "Last 24h",
    last7d: "Last 7d",
    healthy: "Healthy",
    warnings: "Warnings",
    avgCpu: "Avg CPU",
    avgMemory: "Avg Memory",
    requestsPerMin: "Requests/min",
    avgLatency: "Avg Latency",
    overview: "Overview",
    performance: "Performance",
    network: "Network",
    cpuUsage: "CPU Usage (24h)",
    memoryUsage: "Memory Usage (24h)",
    responseLatency: "Response Latency (24h)",
    networkTraffic: "Network Traffic by Server",
    cpu: "CPU",
    memory: "Memory",
    disk: "Disk",
    latency: "Latency",
    uptime: "Uptime",
    reqPerMin: "Req/min",
    viewLogs: "View Logs",
    requestsPerMin24h: "Requests/min (24h)",
    inbound: "Inbound",
    outbound: "Outbound",
    disabled: "Disabled",
    warning: "Warning",
    critical: "Critical",
    offline: "Offline",
    serverLogs: "Server Logs",
    serverLogsTitle: "Server Logs - {name}",
    viewServerLogsDescription: "View server logs including startup time, database connections, memory usage, and request status",
    loadingLogs: "Loading logs...",
    serverStarted: "Server Started",
    databaseConnectionEstablished: "Database Connection Established",
    currentMemoryUsage: "Current Memory Usage",
    totalLogEntries: "Total Log Entries",
    filterByStatus: "Filter by Status",
    allStatuses: "All Statuses",
    statusUp: "UP",
    statusLow: "LOW",
    statusCritical: "CRITICAL",
    statusDown: "DOWN",
    recentLogEntries: "Recent Log Entries",
    showingEntries: "Showing {filtered} of {total} entries",
    showingEntriesStatusChanges: "Showing {filtered} of {total} entries (status changes only)",
    latencyLabel: "Latency",
    noLogsAvailable: "No logs available",
    connectionFailed: "Connection failed - Request may have been queued or failed",
    criticalLatencyDetected: "Critical latency detected - Immediate attention required",
    highLatencyDetected: "High latency detected - Performance may be degraded",
    apiGateway: "API Gateway",
    paymentProcessor: "Payment Processor",
    databasePrimary: "Database Primary",
    databaseReplica: "Database Replica",
    webhookHandler: "Webhook Handler",
    queueProcessor: "Queue Processor",
    loadBalancer: "Load Balancer",
    redisCache: "Redis Cache",
    singapore: "Singapore",
    bangkok: "Bangkok",
    na: "N/A",
    failedToFetchLogs: "Failed to fetch logs",

    // Merchant Panel
    merchantPortal: "Merchant Portal",
    navigation: "Navigation",
    loggedInAs: "Logged in as",

    // Merchant Dashboard
    welcome: "Welcome back",

    // Merchant Settings
    apiKeys: "API Keys",
    yourAuthCredentials: "Your authentication credentials for API access",
    apiKeyPublic: "API Key (Public)",
    secretKey: "Secret Key",
    regenerateKeys: "Regenerate Keys",
    invalidateCurrentKeys: "This will invalidate your current keys",
    regenerate: "Regenerate",
    regenerateApiKeys: "Regenerate API Keys?",
    invalidateImmediately: "This action will invalidate your current API keys immediately. All existing integrations using these keys will stop working until updated.",
    callbackUrls: "Callback URLs",
    whereWeSendNotifications: "Where we send payment notifications",
    callbackUrl: "Callback URL",
    wePostToEndpoint: "We POST transaction updates to this endpoint",
    webhookUrl: "Webhook URL (Optional)",
    additionalEventNotifications: "For additional event notifications",
    saveUrls: "Save URLs",
    merchantProfile: "Merchant Profile",
    yourBusinessInfo: "Your business information",
    businessName: "Business Name",
    emailAddress: "Email Address",
    supportContact: "Support Contact",
    saveProfile: "Save Profile",

    // Engine Panel
    enginePanel: "Engine Panel",

    // Reseller Panel
    resellerPanel: "Reseller Panel",

    // Common form labels
    name: "Name",
    email: "Email",
    phone: "Phone",
    contactName: "Contact Name",
    contactEmail: "Contact Email",
    contactPhone: "Contact Phone",
    notes: "Notes",

    // API related
    apiCredentials: "API Credentials",
    keepSecretConfidential: "Keep your secret key confidential.",
    securityNotice: "Security Notice",
    neverExposeSecretKey: "Never expose it in client-side code or share it publicly.",
    regenerateAllKeys: "Regenerate All Keys",
    invalidateCurrentKeysWarning: "Generate new API keys. Your current keys will stop working immediately.",

    // Withdrawals
    manualPayout: "Manual Payout",
    newWithdrawal: "New Withdrawal",
    history: "History",
    withdrawalRequestSubmittedSuccessfully: "Withdrawal request submitted successfully!",
    noWithdrawalsFound: "No withdrawals found",
    bank: "Bank",
    bankAccountNumber: "Bank Account Number",
    accountHolderName: "Account Holder Name",
    enterAccountHolderName: "Enter account holder name",
    orderId: "Order ID",
    customerId: "Customer ID",
    optionalCustomerRef: "Optional customer reference",
    createPayout: "Create Payout",

    // Dialogs
    areYouSure: "Are you sure?",

    // Login Forms
    pleaseEnterBothEmailAndPassword: "Please enter both email and password",
    loginFailed: "Login failed. Please check your credentials.",
    loginFailedCheckCredentials: "Login failed. Please check your credentials and panel type.",
    pleaseFillInAllFields: "Please fill in all fields",
    rememberMe: "Remember me",
    forgotPassword: "Forgot password?",
    signIn: "Sign In",
    signingIn: "Signing in...",
    signInAsStaff: "Sign In as Staff",
    signInAs: "Sign In as {type}",
    signInToPortal: "Sign In to Portal",
    selectAccountTypeAndEnterCredentials: "Select your account type and enter your credentials",
    welcomeBack: "Welcome back",
    welcomeBackAdmin: "Welcome Back",
    signInToAccessAdminDashboard: "Sign in to access the admin dashboard",
    signInToYourMerchantAccount: "Sign in to your merchant account",
    paymentGatewayAdminPanel: "Payment Gateway Admin Panel",
    accessRestrictedToAuthorizedOnly: "Access restricted to authorized personal only",
    staffMemberClickHereToLogin: "Staff Member? Click here to login",
    enginePortalAccess: "Engine Portal Access",
    partnerPortalAccess: "Partner Portal Access",
    merchantPortal: "Merchant Portal",
    staffMemberLogin: "Staff Member Login",
    staffAccess: "Staff Access",
    signInWithStaffCredentials: "Sign in with your staff credentials",
    panelType: "Panel Type",
    selectYourPanelType: "Select your panel type",
    selectPanelYouWereAssignedTo: "Select the panel you were assigned to",
    adminPanel: "Admin Panel",
    administrativeStaff: "Administrative staff",
    engineStaff: "Engine staff",
    resellerStaff: "Reseller staff",
    merchantStaff: "Merchant staff",
    staffAccessOnly: "Staff access only. Not a staff member?",
    adminLogin: "Admin Login",
    engineLogin: "Engine Login",
    resellerLogin: "Reseller Login",
    merchantLogin: "Merchant Login",
    iAmStaffMember: "I am a staff member",
    needHelpContact: "Need help? Contact",
    needHelpAccessingAccount: "Need help accessing your account?",
    contactSupport: "Contact Support",
    merchantBusinessAccess: "Merchant business access",
    resellerPartnerAccess: "Reseller partner access",
    allRightsReserved: "All rights reserved.",
    privacyPolicy: "Privacy Policy",
    termsOfService: "Terms of Service",

    // Infrastructure
    infrastructure: "Infrastructure",
    apiGateway: "API Gateway",
    database: "Database",
    queue: "Queue",

    // Panel specific
    adminPanel: "Admin Panel",

    // Additional
    total: "total",
    successful: "successful",
    completed: "completed",

    // Transaction fields
    transaction: "Transaction",
    method: "Method",
    ref: "Ref",
    paymentId: "Payment ID",
    clientName: "Client Name",
    clientBankAccount: "Client Bank Account",

    // Filters
    allTypes: "All Types",
    allMerchants: "All Merchants",
    dateRange: "Date Range",
    clearFilters: "Clear Filters",
    exportCsv: "Export CSV",
    searchHeaderPlaceholder: "Search transactions, merchants...",

    // Stats
    vsLastPeriod: "vs last period",
    live: "Live",
    noTransactionsYet: "No transactions yet",
    noProvidersConfigured: "No providers configured",
    noProvidersFound: "No providers found",
    avgResponse: "Avg Response",

    // Routing Rule Form fields
    ruleName: "Rule Name",

    // Withdrawal form
    selectMerchant: "Select merchant",
    selectBank: "Select bank",

    // Priority badge text
    priorityText: "Priority",
    viewOnlyBadge: "View",

    // Additional transaction/common
    retry: "Retry",
    noTransactionsFound: "No transactions found",
    noDeposits: "No deposits",

    // Transaction Detail
    transactionDetails: "Transaction Details",
    transactionInfo: "Transaction Info",
    processingInfo: "Processing Info",
    customerInfo: "Customer Info",
    bankDetails: "Bank Details",
    callbackStatus: "Callback Status",
    timestamps: "Timestamps",
    retryTransaction: "Retry Transaction",
    resendCallback: "Resend Callback",
    routingAttempts: "Routing Attempts",
    attempts: "Attempts",
    lastAttempt: "Last Attempt",
    createdAt: "Created",
    processedAt: "Processed",
    completedAt: "Completed",
    updatedAt: "Updated At",
    currency: "Currency",
    feeAmount: "Fee",
    providerRef: "Provider Ref",
    account: "Account",
    nA: "N/A",
    errorCode: "Error Code",
    qrCode: "QR Code",
    qrPayment: "QR Payment",
    qrPaymentInfo: "QR Payment Info",
    qrCodeInfo: "QR Code Info",
    qrCodeDetails: "QR Code Details",
    qrCodePayment: "QR Code Payment",
    expiryInfo: "Expiry Info",
    expiresAt: "Expires At",
    viewQrCode: "View QR Code",
    expires: "Expires",
    manualStatusUpdate: "Manual Status Update",
    updateStatus: "Update Status",
    newStatus: "New Status",
    adminNoteOptional: "Admin Note (Optional)",
    adminNote: "Admin Note",
    reasonForStatusChange: "Reason for status change...",
    characters: "characters",
    updating: "Updating...",
    statusUpdated: "Status updated",
    transactionStatusChangedTo: "Transaction status changed to",
    updateFailed: "Update failed",
    failedToUpdateTransactionStatus: "Failed to update transaction status",
    webhookInfo: "Webhook Info",
    webhook: "Webhook",
    url: "URL",
    rawWebhookData: "Raw Webhook Data",
    updated: "Updated",
    ip: "IP",
    createdLabel: "Created",
    paymentMethod: "Payment Method",
    hierarchy: "Hierarchy",
    delivered: "Delivered",
    callback: "Callback",

    // Withdrawal Form
    bankTransfer: "Bank Transfer",
    usdtCrypto: "USDT Crypto",
    selectBankLabel: "Select Bank",
    chooseYourBank: "Choose your bank",
    accountNumber: "Account Number",
    enterAccountNumber: "Enter your bank account number",
    enterAccountHolderName: "Enter account holder name",
    amountThb: "Amount (THB)",
    minimumWithdrawal: "Minimum withdrawal",
    withdrawToBank: "Withdraw to Bank",
    networkLabel: "Network",
    walletAddress: "Wallet Address",
    enterWalletAddress: "Enter your {network} USDT address",
    amountUsdt: "Amount (USDT)",
    thbEquivalent: "THB Equivalent",
    exchangeRate: "Exchange Rate",
    withdrawUsdt: "Withdraw USDT",
    withdrawalSummary: "Withdrawal Summary",
    totalWithdrawn: "Total Withdrawn",
    requests: "requests",
    processingTime: "Processing Time",
    hours: "hours",
    mins: "mins",
    needHelp: "Need Help?",
    contactSupport: "Contact support if your withdrawal takes longer than expected.",
    walletWarning: "Please ensure the wallet address is correct and supports",
    incorrectAddressWarning: "Incorrect addresses may result in permanent loss of funds.",

    // Merchant Form
    editMerchant: "Edit Merchant",
    addNewMerchant: "Add New Merchant",
    merchantName: "Merchant Name",
    merchantCode: "Merchant Code",
    merchantInfo: "Merchant Info",
    parentType: "Parent Type",
    directAdmin: "Direct (Admin)",
    enableDeposits: "Enable Deposits",
    allowDepositTransactions: "Allow deposit transactions",
    enableWithdrawals: "Enable Withdrawals",
    allowWithdrawalTransactions: "Allow withdrawal transactions",
    apiSettings: "API Settings",
    limitsFees: "Limits & Fees",
    contact: "Contact",
    securityNote: "API keys are shown only once. Store them securely.",
    ipWhitelist: "IP Whitelist",
    noIpsWhitelisted: "No IPs whitelisted",
    rateLimit: "Rate Limit (requests/minute)",
    minDeposit: "Min Deposit",
    maxDeposit: "Max Deposit",
    minWithdrawal: "Min Withdrawal",
    maxWithdrawal: "Max Withdrawal",
    dailyDepositLimit: "Daily Deposit Limit",
    dailyWithdrawalLimit: "Daily Withdrawal Limit",
    feeType: "Fee Type",
    feeValue: "Fee Value",
    percentage: "Percentage",
    fixed: "Fixed",
    tiered: "Tiered",
    settlementPeriod: "Settlement Period",
    sameDay: "Same Day",
    nextDay: "Next Day",
    weekly: "Weekly",
    updateMerchant: "Update Merchant",
    createMerchantBtn: "Create Merchant",
    emailCannotBeChanged: "Email cannot be changed",
    password: "Password",
    changePassword: "Change Password",
    leaveBlankToKeepPassword: "Leave blank to keep current password",
    changePasswordDescription: "Enter a new password to change it, or leave blank to keep the current password",
    selectEngine: "Select Engine",
    selectReseller: "Select Reseller",
    whoManagesMerchant: "Who manages this merchant",
    engine: "Engine",
    reseller: "Reseller",
    notes: "Notes",
    internalNotes: "Internal notes...",
    security: "Security",
    apiKeysShownOnce: "API keys are shown only once. Store them securely.",
    regenerate: "Regenerate",
    secretKey: "Secret Key",
    callbackUrl: "Callback URL",
    webhookUrl: "URL เว็บฮุค",
    ipWhitelistPlaceholder: "e.g., 192.168.1.1",
    minDepositThb: "Min Deposit (THB)",
    maxDepositThb: "Max Deposit (THB)",
    minWithdrawalThb: "Min Withdrawal (THB)",
    maxWithdrawalThb: "Max Withdrawal (THB)",
    dailyDepositLimitThb: "Daily Deposit Limit (THB)",
    dailyWithdrawalLimitThb: "Daily Withdrawal Limit (THB)",
    feeValueLabel: "Fee Value",
    contactName: "Contact Name",
    contactPhone: "Contact Phone",
    backButton: "← Back",
    nextButton: "Next →",
    merchantNamePlaceholder: "e.g., Gaming Site A",
    merchantCodePlaceholder: "e.g., MGC001",
    emailPlaceholder: "merchant@example.com",
    passwordPlaceholder: "Enter password",
    callbackUrlPlaceholder: "https://your-site.com/api/payment/callback",
    webhookUrlPlaceholder: "https://your-site.com/api/payment/webhook",
    contactNamePlaceholder: "John Doe",
    contactEmailPlaceholder: "john@example.com",
    contactPhonePlaceholder: "+66 XXX XXX XXXX",
    t0SameDay: "T+0 (Same Day)",
    t1NextDay: "T+1 (Next Day)",
    t3: "T+3",
    t7Weekly: "T+7 (Weekly)",
    apiKeyPublic: "API Key (Public)",
    createMerchantUnderReseller: "Create a new merchant under your reseller account",
    commissionGivenToYouBy: "This is the commission rate given to you by {name}. You can assign a commission to this merchant.",
    feeValueSetToBy: "This fee value is set to {value}% (the commission rate given to you by {name}). This cannot be changed.",

    // Provider Form
    editProvider: "Edit Provider",
    addNewProvider: "Add New Provider",
    providerNameRequired: "Provider Name *",
    providerCodeRequired: "Provider Code *",
    providerNamePlaceholder: "e.g., SCB",
    providerCodePlaceholder: "e.g., SCB",
    providerTypeEwallet: "E-Wallet",
    providerTypeQr: "QR Payment",
    providerTypeCard: "Card",
    providerName: "Provider Name",
    providerCode: "Provider Code",
    supportsDeposits: "Supports Deposits",
    processDepositTransactions: "Process deposit transactions",
    supportsWithdrawals: "Supports Withdrawals",
    processWithdrawalTransactions: "Process withdrawal transactions",
    supportsQr: "Supports QR Payments",
    generatePromptPayQr: "Generate PromptPay QR codes",
    logoUrl: "Logo URL",
    logoUrlPlaceholder: "https://...",
    apiConfig: "API Config",
    routingLimits: "Routing & Limits",
    apiEndpoint: "API Endpoint",
    apiEndpointPlaceholder: "https://api.provider.com/v1",
    apiKey: "API Key",
    apiSecret: "API Secret",
    webhookSecret: "Webhook Secret",
    providerIpWhitelist: "Provider IP Whitelist (for webhooks)",
    ipWhitelistPlaceholder: "e.g., 203.0.113.1",
    priorityHighest: "Priority (1 = highest)",
    weightLoadBalancing: "Weight (load balancing)",
    dailyLimit: "Daily Limit",
    minAmountThb: "Min Amount (THB)",
    maxAmountThb: "Max Amount (THB)",
    dailyLimitThb: "Daily Limit (THB)",
    feePercentage: "Fee Percentage",
    settlementTimeLabel: "Settlement Time",
    instant: "Instant",
    oneHour: "1 Hour",
    updateProvider: "Update Provider",
    addProviderBtn: "Add Provider",

    // Common
    to: "to",

    // Engine Panel specific
    welcomeBack: "Welcome back",
    totalVolume: "Total Volume",
    volumeOverviewTitle: "Volume Overview",
    last7DaysVolume: "Last 7 days transaction volume",
    recent: "Recent",
    noMerchantsFound: "No merchants found",
    noMerchantsMatchSearch: "No merchants match your search",
    todaysVolume: "Today's Volume",
    volumeTrend7Days: "Volume Trend (7 Days)",
    apiAndSettings: "API & Settings",
    avgDeposit: "Avg Deposit",
    searchMerchantsPlaceholder: "Search merchants...",
    merchantsRegisteredCount: "merchants registered",
    partnersCount: "partners",
    addReseller: "Add Reseller",
    noResellers: "No resellers",
    viewOnly: "View Only",
    paymentProviderStatus: "Payment provider status (managed by admin)",
    searchProviders: "Search providers...",
    configureProviderSelection: "Configure provider selection and failover logic",
    howRoutingWorks: "How Routing Works",
    routingExplanation: "Rules are evaluated in priority order. The first matching rule determines which providers handle the transaction. If a provider fails, the system automatically fails over to the next in the sequence.",
    noRoutingRules: "No routing rules configured",
    createFirstRoutingRule: "Create your first routing rule to start directing transactions",
    failover: "Failover",
    logs: "logs",
    noLogs: "No logs",
    payload: "Payload",
    noSettlementsFound: "No settlements",
    manageProfile: "Manage your reseller profile and API credentials",
    resellerProfile: "Reseller Profile",
    yourBusinessInfo: "Your business information",
    resellerCode: "Reseller Code",
    supportEmail: "Support Email",
    apiCredentialsTitle: "API Credentials",
    yourApiKeysIntegration: "Your API keys for integration",
    regenerateApiKeysTitle: "Regenerate API Keys",
    regenerateApiKeysDesc: "Generate new API keys. Your current keys will stop working immediately.",
    regenerateApiKeysConfirm: "This will immediately invalidate your current API keys. All integrations using the old keys will stop working. Make sure to update your server configuration with the new keys.",
    regenerateKeys: "Regenerate Keys",
    requestPayoutsViaBankUsdt: "Request payouts via Bank or USDT",
    clear: "Clear",
    allowedProviders: "Allowed Providers",
    feeConfiguration: "Fee Configuration",
    percentageFeeLabel: "Percentage Fee (%)",
    feesAndCommission: "Fees & Commission",
    commissionRatesAndFeeInfo: "Commission rates and fee information",
    givenBy: "Given by",
    depositCommission: "Deposit Commission",
    withdrawalCommission: "Withdrawal Commission",
    yourCommissionRates: "Your Commission Rates",
    ratesAssignedToYou: "These are the rates assigned to you",
    suspendMerchantConfirm: "Are you sure you want to suspend",
    willNotProcessTransactions: "They will not be able to process transactions.",
    activateMerchantConfirm: "Are you sure you want to activate",
    suspendMerchantTitle: "Suspend Merchant",
    activateMerchantTitle: "Activate Merchant",
    createNewMerchant: "Create New Merchant",
    businessNameLabel: "Business Name",
    emailLabel: "Email",
    callbackUrlLabel: "Callback URL",
    // Additional Merchant Panel
    newDeposit: "New Deposit",
    generateQrDeposit: "Generate QR Deposit",
    createPromptPayQr: "Create PromptPay QR code for customers",
    howItWorks: "How it works",
    enterDepositAmount: "Enter the deposit amount",
    generateQr: "Generate QR Code",
    customerScansQr: "Customer scans QR",
    paymentProcessed: "Payment processed",
    waitingForPayment: "Waiting for payment",
    statusAutoRefresh: "Status will auto-refresh",
    paymentReceived: "Payment Received",
    qrCodeExpired: "QR Code Expired",
    generateNewQr: "Generate New QR",

    // Transaction Detail Sheet
    statusTimeline: "Status Timeline",
    createdLabel: "Created",
    processingLabel: "Processing",
    completedLabel: "Completed",
    failedLabel: "Failed",
    error: "Error",
    reference: "Reference",

    // Provider Page
    maintenance: "Maintenance",
    degraded: "Degraded",

    // Settlement Page
    totalSettled: "Total Settled",
    pendingSettlement: "Pending Settlement",
    totalRecords: "Total Records",
    transactionVolume: "Transaction Volume",
    last7DaysPerformance: "Last 7 days performance",
    transactionPerformance: "Transaction performance",
    welcomeBackOverview: "Welcome back! Here's your overview.",
    noEmail: "No email",

    // Additional missing translations
    searchByWebhookId: "Search by webhook ID, transaction ID, source...",
    selectEngine: "Select Engine",
    selectPanelType: "Select your panel type",
    enterPassword: "Enter password",
    enterNewPassword: "Enter new password",
    searchRoles: "Search roles...",
    searchStaff: "Search staff members...",
    searchBySettlementId: "Search by settlement ID...",
    searchBySettlementIdPlaceholder: "Search by settlement ID...",
    searchByTransactionId: "Search by transaction ID...",
    defaultQrRoute: "e.g., Default QR Route",
    describeRole: "Describe what this role can do...",
    orderIdPlaceholder: "Order ID, Customer ID, etc.",
    amountPlaceholder: "0.00",
    accountNumberPlaceholder: "e.g., 1234567890",
    accountHolderPlaceholder: "Full name as on bank account",
    callbackUrlPlaceholder: "https://your-site.com/api/callback",
    webhookUrlPlaceholder: "https://your-site.com/api/webhook",
    phonePlaceholder: "+66 XXX XXX XXXX",
    searchMerchantsPlaceholder: "Search merchants...",
    orderIdLabel: "Order ID",
    customerIdLabel: "Customer ID",
    orderIdPlaceholder: "Enter Order ID",
    customerIdPlaceholder: "Enter Customer ID",

    // Admin Roles
    staffRoles: "Staff Roles",
    rolesCount: "roles",
    customCount: "custom",
    systemCount: "system",
    createRole: "Create Role",
    editRole: "Edit Role",
    updateRoleDetailsAndPermissions: "Update role details and permissions",
    defineNewRoleWithPermissions: "Define a new role with specific permissions",
    basicDetailsForRole: "Basic details for the role",
    roleNameRequired: "Role Name *",
    merchantManagerPlaceholder: "e.g., Merchant Manager",
    describeWhatRoleCanDo: "Describe what this role can do...",
    roleCreatedForAdminPanel: "This role will be created for the Admin panel",
    selectActionsRoleCanPerform: "Select what actions this role can perform",
    selectAll: "Select All",
    deselectAll: "Deselect All",
    noPermissionsSelected: "No permissions selected",
    permissionsSelected: "{count} permissions selected",
    permissionSelected: "1 permission selected",
    staffWithRoleActions: "Staff members with this role will be able to perform these actions",
    updateRole: "Update Role",
    provideRoleNameAndPermission: "Please provide a role name and select at least one permission",
    roleEditingNotImplemented: "Role editing is not yet implemented. Please delete and recreate the role.",
    searchRolesPlaceholder: "Search roles...",
    customRoles: "Custom Roles",
    rolesYouveCreated: "Roles you've created",
    role: "Role",
    permissions: "Permissions",
    noPermissions: "No permissions",
    morePermissions: "more",
    systemRoles: "System Roles",
    predefinedRolesReadOnly: "Predefined roles (read-only)",
    systemBadge: "System",
    noRolesFound: "No roles found",
    tryDifferentSearchTerm: "Try a different search term",
    getStartedCreatingFirstRole: "Get started by creating your first role",
    deleteRole: "Delete Role",
    deleteRoleConfirmation: "Are you sure you want to delete the role \"{name}\"? This action cannot be undone. Staff members with this role will lose access.",
    systemRolesCannotBeDeleted: "System roles cannot be deleted",
    generalInformation: "General Information",
    description: "Description",
    panelTypeRequired: "Panel Type *",
    adminPanel: "Admin",
    permissionsTitle: "Permissions",
    cancel: "Cancel",
    saving: "Saving...",
    save: "Save",
    createRoleAction: "Create Role",

    // Permissions
    "permission.merchants.view": "View Merchants",
    "permission.merchants.create": "Create Merchants",
    "permission.merchants.edit": "Edit Merchants",
    "permission.merchants.delete": "Delete Merchants",
    "permission.engines.view": "View Engines",
    "permission.engines.create": "Create Engines",
    "permission.engines.edit": "Edit Engines",
    "permission.engines.delete": "Delete Engines",
    "permission.resellers.view": "View Resellers",
    "permission.resellers.create": "Create Resellers",
    "permission.resellers.edit": "Edit Resellers",
    "permission.resellers.delete": "Delete Resellers",
    "permission.transactions.view": "View Transactions",
    "permission.transactions.create": "Create Transactions",
    "permission.transactions.edit": "Edit Transactions",
    "permission.staff.view": "View Staff",
    "permission.staff.create": "Create Staff",
    "permission.staff.edit": "Edit Staff",
    "permission.staff.delete": "Delete Staff",
    "permission.settings.view": "View Settings",
    "permission.settings.edit": "Edit Settings",
    "permission.roles.view": "View Roles",
    "permission.roles.create": "Create Roles",
    "permission.roles.edit": "Edit Roles",
    "permission.roles.delete": "Delete Roles",

    // System Role Names
    "role.Super Admin": "Super Admin",
    "role.Admin Manager": "Admin Manager",
    "role.Engine Manager": "Engine Manager",
    "role.Reseller Manager": "Reseller Manager",
    "role.Merchant Manager": "Merchant Manager",
    "role.Transaction Viewer": "Transaction Viewer",
    "role.Staff Manager": "Staff Manager",

    // System Role Descriptions
    "roleDesc.Full access to all admin functions": "Full access to all admin functions",
    "roleDesc.Manage admin operations": "Manage admin operations",
    "roleDesc.Manage engines and their merchants": "Manage engines and their merchants",
    "roleDesc.Manage resellers and their merchants": "Manage resellers and their merchants",
    "roleDesc.Manage merchants": "Manage merchants",
    "roleDesc.View transactions only": "View transactions only",
    "roleDesc.Manage staff members": "Manage staff members",

    // Admin Staff Form
    editStaffMember: "Edit Staff Member",
    updateStaffMemberDetails: "Update staff member details",
    addNewStaffMemberToOrganization: "Add a new staff member to your organization",
    basicDetailsForStaffMember: "Basic details for the staff member",
    fullName: "Full Name",
    emailAddress: "Email Address",
    newPasswordLeaveBlank: "New Password (leave blank to keep current)",
    roleAssignment: "Role Assignment",
    assignRolesToControlAccess: "Assign roles to control what this staff member can access",
    noRolesAvailableCreateFirst: "No roles available. Please create roles first.",
    updateStaffMember: "Update Staff Member",
    loadingUserInformation: "Loading user information...",
    unableToGetCurrentUserId: "Unable to get current user ID. Please refresh the page and try again.",
    pleaseFillAllRequiredFields: "Please fill in all required fields.",
    pleaseProvidePassword: "Please provide a password for the new staff member.",
    passwordMustBeAtLeast6Characters: "Password must be at least 6 characters long.",
  },
  th: {
    // Common
    dashboard: "แดชบอร์ด",
    transactions: "ธุรกรรม",
    deposits: "ฝากเงิน",
    withdrawals: "ถอนเงิน",
    merchants: "ร้านค้า",
    providers: "ผู้ให้บริการ",
    routing: "การกำหนดเส้นทาง",
    webhooks: "เว็บฮุค",
    settlements: "การชำระบัญชี",
    settings: "การตั้งค่า",
    engines: "เอนจิ้น",
    resellers: "ตัวแทนจำหน่าย",
    servers: "เซิร์ฟเวอร์",
    allTransactions: "ธุรกรรมทั้งหมด",
    staff: "พนักงาน",
    staffMembers: "สมาชิกพนักงาน",
    staffMembersCount: "สมาชิกพนักงาน",
    staffMember: "สมาชิกพนักงาน",
    createStaffMember: "สร้างสมาชิกพนักงาน",
    noStaffMembersFound: "ไม่พบสมาชิกพนักงาน",
    noRolesAssigned: "ยังไม่ได้กำหนดบทบาท",
    changeStaffStatus: "เปลี่ยนสถานะพนักงาน",
    deleteStaffMember: "ลบสมาชิกพนักงาน",
    areYouSureYouWantTo: "คุณแน่ใจหรือไม่ที่จะ",
    actions: "การดำเนินการ",
    roles: "บทบาท",
    description: "คำอธิบาย",

    // Actions
    save: "บันทึก",
    cancel: "ยกเลิก",
    delete: "ลบ",
    edit: "แก้ไข",
    add: "เพิ่ม",
    create: "สร้าง",
    update: "อัปเดต",
    view: "ดู",
    search: "ค้นหา",
    filter: "กรอง",
    export: "ส่งออก",
    import: "นำเข้า",
    refresh: "รีเฟรช",
    reset: "รีเซ็ต",
    submit: "ส่ง",
    confirm: "ยืนยัน",
    close: "ปิด",
    back: "กลับ",
    next: "ถัดไป",
    previous: "ก่อนหน้า",
    loading: "กำลังโหลด...",
    saving: "กำลังบันทึก...",
    creating: "กำลังสร้าง...",
    processing: "กำลังดำเนินการ...",

    // Status
    active: "ใช้งาน",
    inactive: "ไม่ใช้งาน",
    pending: "รอดำเนินการ",
    success: "สำเร็จ",
    failed: "ล้มเหลว",
    expired: "หมดอายุ",
    cancelled: "ยกเลิก",
    declined: "ปฏิเสธ",
    suspended: "ระงับ",
    terminated: "ยุติ",
    completed: "เสร็จสิ้น",

    // Time
    today: "วันนี้",
    thisDate: "วันนี้",
    thisWeek: "สัปดาห์นี้",
    thisMonth: "เดือนนี้",
    thisYear: "ปีนี้",
    customRange: "ช่วงกำหนดเอง",
    selectRange: "เลือกช่วง",

    // Navigation
    logout: "ออกจากระบบ",
    profile: "โปรไฟล์",
    notifications: "การแจ้งเตือน",
    markAllRead: "อ่านทั้งหมด",
    viewAllNotifications: "ดูการแจ้งเตือนทั้งหมด",

    // Dashboard
    paymentOrchestration: "ภาพรวมระบบการชำระเงิน",
    totalDeposits: "ยอดฝากทั้งหมด",
    totalWithdrawals: "ยอดถอนทั้งหมด",
    successRate: "อัตราสำเร็จ",
    activeMerchants: "ร้านค้าที่ใช้งาน",
    quickActions: "การดำเนินการด่วน",
    addMerchant: "เพิ่มร้านค้า",
    onboardNewMerchant: "เพิ่มร้านค้าใหม่",
    configureProvider: "ตั้งค่าผู้ให้บริการ",
    addPaymentProvider: "เพิ่มผู้ให้บริการชำระเงิน",
    routingRules: "กฎการกำหนดเส้นทาง",
    configureRouting: "ตั้งค่าการกำหนดเส้นทาง",
    pendingTransactions: "ธุรกรรมรอดำเนินการ",
    requiresAttention: "ต้องการความสนใจ",
    volumeOverview: "ภาพรวมปริมาณ",
    last7Days: "ปริมาณธุรกรรม 7 วันล่าสุด",
    recentTransactions: "ธุรกรรมล่าสุด",
    viewAll: "ดูทั้งหมด",
    totalVolume: "ปริมาณทั้งหมด",
    volumeOverviewTitle: "ภาพรวมปริมาณ",
    transactionVolume: "ปริมาณธุรกรรม",
    allStatus: "ทุกสถานะ",

    // Transactions
    transactionsFound: "ธุรกรรมที่พบ",
    transactionId: "รหัสธุรกรรม",
    merchantRef: "อ้างอิงร้านค้า",
    type: "ประเภท",
    amount: "จำนวนเงิน",
    status: "สถานะ",
    date: "วันที่",
    provider: "ผู้ให้บริการ",
    deposit: "ฝาก",
    withdrawal: "ถอน",
    showing: "แสดง",
    to: "ถึง",
    of: "จาก",
    page: "หน้า",

    // Merchants
    merchantsRegistered: "ร้านค้าที่ลงทะเบียน",
    searchMerchants: "ค้นหาร้านค้า...",
    merchant: "ร้านค้า",
    code: "รหัส",
    parent: "หน่วยงานหลัก",
    features: "ฟีเจอร์",
    fee: "ค่าธรรมเนียม",
    settlement: "การชำระบัญชี",
    direct: "ตรง",
    viewDetails: "ดูรายละเอียด",
    viewApiKeys: "ดู API Keys",
    suspend: "ระงับ",
    activate: "เปิดใช้งาน",
    deleteMerchant: "ลบร้านค้า",
    deleteConfirmation: "คุณแน่ใจหรือไม่ที่จะลบ",
    cannotBeUndone: "การดำเนินการนี้ไม่สามารถยกเลิกได้",
    suspendMerchantTitle: "ระงับร้านค้า",
    activateMerchantTitle: "เปิดใช้งานร้านค้า",
    suspendMerchantConfirm: "คุณแน่ใจหรือไม่ที่จะระงับ",
    activateMerchantConfirm: "คุณแน่ใจหรือไม่ที่จะเปิดใช้งาน",
    willNotProcessTransactions: "พวกเขาจะไม่สามารถดำเนินการธุรกรรมได้",
    noMerchantsFound: "ไม่พบร้านค้า",

    // Providers
    paymentProviders: "ผู้ให้บริการชำระเงิน",
    activeProviders: "ผู้ให้บริการที่ใช้งาน",
    searchProviders: "ค้นหาผู้ให้บริการ...",
    addProvider: "เพิ่มผู้ให้บริการ",
    healthScore: "คะแนนสุขภาพ",
    response: "การตอบสนอง",
    priority: "ลำดับความสำคัญ",
    disable: "ปิดใช้งาน",
    enable: "เปิดใช้งาน",
    deleteProvider: "ลบผู้ให้บริการ",
    affectRouting: "จะส่งผลต่อกฎการกำหนดเส้นทาง",

    // Routing
    routingRulesTitle: "กฎการกำหนดเส้นทาง",
    configureProviderSelection: "ตั้งค่าการเลือกผู้ให้บริการและตรรกะการสำรอง",
    addRule: "เพิ่มกฎ",
    howRoutingWorks: "การกำหนดเส้นทางทำงานอย่างไร",
    routingDescription: "กฎจะถูกประเมินตามลำดับความสำคัญ กฎแรกที่ตรงกันจะกำหนดว่าผู้ให้บริการใดจะจัดการธุรกรรม",
    noRoutingRules: "ยังไม่มีกฎการกำหนดเส้นทาง",
    viewTransactionRoutingConfig: "ดูการกำหนดค่าเส้นทางธุรกรรม",
    failover: "การสำรอง",
    amountRange: "ช่วงจำนวนเงิน",
    createFirstRule: "สร้างกฎการกำหนดเส้นทางแรกของคุณเพื่อเริ่มนำทางธุรกรรม",
    defaultRule: "กฎเริ่มต้น",
    ruleName: "ชื่อกฎ",
    transactionType: "ประเภทธุรกรรม",
    paymentMethod: "วิธีการชำระเงิน",
    minAmount: "จำนวนขั้นต่ำ",
    maxAmount: "จำนวนสูงสุด",
    specificMerchant: "ร้านค้าเฉพาะ (ไม่บังคับ)",
    allMerchants: "ร้านค้าทั้งหมด",
    routingMode: "โหมดการกำหนดเส้นทาง",
    providerSequence: "ลำดับผู้ให้บริการ (คลิกเพื่อเพิ่ม/ลบ)",
    clickProvidersOrder: "คลิกผู้ให้บริการตามลำดับที่คุณต้องการใช้ ตัวเลขแสดงลำดับความสำคัญ",
    enableFailover: "เปิดใช้งานการสำรอง",
    autoTryNextProvider: "ลองผู้ให้บริการถัดไปอัตโนมัติเมื่อล้มเหลว",
    maxFailoverAttempts: "จำนวนครั้งสูงสุดของการสำรอง",
    allTypes: "ทุกประเภท",
    depositsOnly: "เฉพาะฝากเงิน",
    withdrawalsOnly: "เฉพาะถอนเงิน",
    allMethods: "ทุกวิธี",
    priorityMode: "ลำดับความสำคัญ (ใช้อันแรกที่พร้อม)",
    roundRobin: "วนรอบ (กระจายเท่าๆ กัน)",
    weighted: "ถ่วงน้ำหนัก (ตามน้ำหนักผู้ให้บริการ)",
    leastLoad: "โหลดน้อยที่สุด (ตามปริมาณปัจจุบัน)",
    editRoutingRule: "แก้ไขกฎการกำหนดเส้นทาง",
    createRoutingRule: "สร้างกฎการกำหนดเส้นทาง",
    deleteRoutingRule: "ลบกฎการกำหนดเส้นทาง",
    affectTransactionRouting: "อาจส่งผลต่อการกำหนดเส้นทางธุรกรรม",

    // Webhook Logs
    webhookLogs: "บันทึกเว็บฮุค",
    webhookEvents: "เหตุการณ์เว็บฮุค",
    webhookId: "รหัสเว็บฮุค",
    direction: "ทิศทาง",
    source: "แหล่งที่มา",
    event: "เหตุการณ์",
    inbound: "ขาเข้า",
    outbound: "ขาออก",
    allDirection: "ทุกทิศทาง",
    allStatus: "ทุกสถานะ",
    received: "ได้รับ",
    validated: "ตรวจสอบแล้ว",
    processed: "ดำเนินการแล้ว",
    invalidSignature: "ลายเซ็นไม่ถูกต้อง",
    invalid_signature: "ลายเซ็นไม่ถูกต้อง",
    webhook: "เว็บฮุค",
    webhookLog: "บันทึกเว็บฮุค",
    webhookDetails: "รายละเอียดเว็บฮุค",
    requestHeaders: "ส่วนหัวคำขอ",
    requestPayload: "ข้อมูลคำขอ",
    responseStatus: "การตอบสนอง",
    noWebhookLogs: "ไม่พบบันทึกเว็บฮุค",
    duplicate: "ซ้ำ",
    failedToLoadWebhookLogs: "โหลดบันทึกเว็บฮุคไม่สำเร็จ",
    noResponseBody: "ไม่มีเนื้อหาการตอบสนอง",

    // Settlements
    settlementTitle: "การชำระบัญชี",
    merchantPayoutManagement: "การจัดการการจ่ายเงินร้านค้า",
    createSettlement: "สร้างการชำระบัญชี",
    searchSettlements: "ค้นหาการชำระบัญชี...",
    settlementId: "รหัสการชำระบัญชี",
    period: "ช่วงเวลา",
    netAmount: "จำนวนสุทธิ",
    process: "ดำเนินการ",
    markCompleted: "ทำเครื่องหมายเสร็จสิ้น",
    exportReport: "ส่งออกรายงาน",
    periodStart: "เริ่มต้นช่วงเวลา",
    periodEnd: "สิ้นสุดช่วงเวลา",
    totalFees: "ค่าธรรมเนียมทั้งหมด",
    noSettlements: "ไม่พบการชำระบัญชี",

    // Settings
    settingsTitle: "การตั้งค่า",
    platformConfig: "การกำหนดค่าและการตั้งค่าแพลตฟอร์ม",
    saveChanges: "บันทึกการเปลี่ยนแปลง",
    general: "ทั่วไป",
    security: "ความปลอดภัย",
    callbacks: "การเรียกกลับ",
    qrSettings: "การตั้งค่า QR",
    notificationsSettings: "การแจ้งเตือน",
    generalSettings: "การตั้งค่าทั่วไป",
    basicPlatformConfig: "การกำหนดค่าแพลตฟอร์มพื้นฐาน",
    platformName: "ชื่อแพลตฟอร์ม",
    timezone: "เขตเวลา",
    defaultCurrency: "สกุลเงินเริ่มต้น",
    securitySettings: "การตั้งค่าความปลอดภัย",
    apiSecurityConfig: "การกำหนดค่าความปลอดภัย API และการยืนยันตัวตน",
    requireIpWhitelist: "ต้องมี IP Whitelist",
    acceptApiCallsFromWhitelistedIps: "รับ API calls จาก IP ที่อยู่ใน whitelist เท่านั้น",
    hmacAlgorithm: "อัลกอริทึม HMAC",
    signatureHeaderName: "ชื่อ Header ลายเซ็น",
    timestampTolerance: "ความคลาดเคลื่อนของเวลา (วินาที)",
    rejectOldRequests: "ปฏิเสธคำขอที่มี timestamp เก่ากว่าค่านี้",
    callbackSettings: "การตั้งค่าการเรียกกลับ",
    configureCallback: "กำหนดค่าพฤติกรรมการเรียกกลับของร้านค้า",
    enableCallbackRetry: "เปิดใช้งานการลองใหม่",
    autoRetryFailedCallbacks: "ลองใหม่อัตโนมัติสำหรับ callbacks ที่ล้มเหลว",
    maxRetryAttempts: "จำนวนครั้งลองสูงสุด",
    retryDelaySchedule: "ตารางหน่วงเวลาลองใหม่ (วินาที)",
    callbackTimeout: "หมดเวลาการเรียกกลับ (วินาที)",
    qrPaymentSettings: "การตั้งค่าการชำระเงิน QR",
    promptPayConfig: "การกำหนดค่า QR Code PromptPay",
    defaultQrExpiry: "เวลาหมดอายุ QR เริ่มต้น (นาที)",
    minimumAmount: "จำนวนขั้นต่ำ (บาท)",
    maximumAmount: "จำนวนสูงสุด (บาท)",
    supportedBanks: "ธนาคารที่รองรับ",
    enableInProviderSettings: "เปิดใช้งานในการตั้งค่าผู้ให้บริการ",
    notificationSettings: "การตั้งค่าการแจ้งเตือน",
    configureAlerts: "กำหนดค่าการแจ้งเตือน",
    emailNotifications: "การแจ้งเตือนอีเมล",
    sendAlertsViaEmail: "ส่งการแจ้งเตือนผ่านอีเมล",
    slackNotifications: "การแจ้งเตือน Slack",
    sendAlertsToSlack: "ส่งการแจ้งเตือนไปยังช่อง Slack",
    alertThreshold: "เกณฑ์การแจ้งเตือน (บาท)",
    sendAlertAboveAmount: "ส่งการแจ้งเตือนสำหรับธุรกรรมที่มากกว่าจำนวนนี้",
    alertTypes: "ประเภทการแจ้งเตือน",
    highValueTransactions: "ธุรกรรมมูลค่าสูง",
    providerFailures: "ความล้มเหลวของผู้ให้บริการ",
    failedCallbackRetries: "การลองใหม่ที่ล้มเหลว",
    suspiciousActivity: "การตรวจจับกิจกรรมที่น่าสงสัย",

    // Engine Settings
    feesAndCommission: "ค่าธรรมเนียมและคอมมิชชั่น",
    feesAndCommissionDesc: "อัตราค่าคอมมิชชั่นและข้อมูลค่าธรรมเนียม",
    givenBy: "กำหนดโดย {type}: {name}",
    yourCommissionRates: "อัตราค่าคอมมิชชั่นของคุณ",
    yourCommissionRatesDesc: "นี่คืออัตราที่กำหนดให้คุณ",
    depositCommission: "ค่าคอมมิชชั่นการฝาก",
    withdrawalCommission: "ค่าคอมมิชชั่นการถอน",
    percentageFee: "ค่าธรรมเนียมเปอร์เซ็นต์",
    depositCommissionPercent: "ค่าคอมมิชชั่นการฝาก (%)",
    withdrawalCommissionPercent: "ค่าคอมมิชชั่นการถอน (%)",
    resellerProfile: "โปรไฟล์ตัวแทนจำหน่าย",
    manageProfile: "จัดการการตั้งค่าโปรไฟล์ของคุณ",
    apiCredentialsTitle: "ข้อมูลรับรอง API",
    yourApiKeysIntegration: "กุญแจ API ของคุณสำหรับการเชื่อมต่อ",
    regenerateApiKeysTitle: "สร้างกุญแจใหม่",
    regenerateApiKeysDesc: "การดำเนินการนี้จะทำให้กุญแจ API ปัจจุบันของคุณใช้งานไม่ได้ทันที",
    regenerateApiKeysConfirm: "คุณแน่ใจหรือไม่ว่าต้องการสร้างกุญแจ API ใหม่? การดำเนินการนี้ไม่สามารถย้อนกลับได้",

    // Engines
    enginePartners: "พันธมิตรเอนจิ้น",
    searchEngines: "ค้นหาเอนจิ้น...",
    createEngine: "สร้างเอนจิ้น",
    engine: "เอนจิ้น",
    volume: "ปริมาณ",
    created: "สร้างเมื่อ",
    suspendEngine: "ระงับเอนจิ้น",
    activateEngine: "เปิดใช้งานเอนจิ้น",
    allResellersAffected: "ตัวแทนจำหน่ายทั้งหมดภายใต้จะได้รับผลกระทบ",
    deleteEngine: "ลบเอนจิ้น",
    editEngine: "แก้ไขเอนจิ้น",
    createNewEngine: "สร้างเอนจิ้นใหม่",
    updateEngineDetails: "อัปเดตรายละเอียดเอนจิ้น",
    setUpNewEnginePartner: "ตั้งค่าเอนจิ้นพันธมิตรใหม่",
    generalTab: "ทั่วไป",
    feesLimitsTab: "ค่าธรรมเนียม & ขีดจำกัด",
    permissionsTab: "สิทธิ์",
    apiAccessTab: "การเข้าถึง API",
    generalInformation: "ข้อมูลทั่วไป",
    businessNameRequired: "ชื่อธุรกิจ *",
    engineCode: "รหัสเอนจิ้น",
    loginEmailRequired: "อีเมลเข้าสู่ระบบ *",
    newPasswordOptional: "รหัสผ่านใหม่ (ไม่บังคับ)",
    passwordRequired: "รหัสผ่าน *",
    leaveBlankToKeepCurrent: "เว้นว่างไว้เพื่อเก็บรหัสผ่านปัจจุบัน",
    enterPassword: "กรอกรหัสผ่าน",
    status: "สถานะ",
    // Status
    active: "ใช้งาน",
    inactive: "ไม่ใช้งาน",
    pending: "รอดำเนินการ",
    success: "สำเร็จ",
    failed: "ล้มเหลว",
    expired: "หมดอายุ",
    cancelled: "ยกเลิก",
    declined: "ถูกปฏิเสธ",
    suspended: "ระงับ",
    terminated: "ยุติการให้บริการ",
    completed: "เสร็จสมบูรณ์",
    maintenance: "ปิดปรับปรุง",
    degraded: "ประสิทธิภาพลดลง",
    ewallet: "อีวอลเล็ท",
    card: "บัตรเครดิต/เดบิต",
    qr: "คิวอาร์เพย์เมนต์",
    deposit: "ฝากเงิน",
    withdrawal: "ถอนเงิน",
    contactName: "ชื่อผู้ติดต่อ",
    contactPhone: "โทรศัพท์ติดต่อ",
    supportEmail: "อีเมลสนับสนุน",
    notes: "หมายเหตุ",
    configureFeeStructureLimits: "ตั้งค่าโครงสร้างค่าธรรมเนียมและขีดจำกัดการดำเนินงาน",
    depositFeeThb: "ค่าธรรมเนียมฝาก (฿)",
    withdrawalFeeThb: "ค่าธรรมเนียมถอน (฿)",
    percentageFeePercent: "ค่าธรรมเนียมเปอร์เซ็นต์ (%)",
    commissionRatePercent: "อัตราค่าคอมมิชชั่น (%)",
    maxResellers: "ตัวแทนจำหน่ายสูงสุด",
    maxMerchants: "ร้านค้าสูงสุด",
    allowedPaymentProviders: "ผู้ให้บริการชำระเงินที่อนุญาต",
    controlEngineActions: "ควบคุมการดำเนินการที่เอนจิ้นสามารถทำได้",
    canCreateResellers: "สามารถสร้างตัวแทนจำหน่าย",
    allowCreatingNewResellerAccounts: "อนุญาตให้สร้างบัญชีตัวแทนจำหน่ายใหม่",
    canCreateMerchants: "สามารถสร้างร้านค้า",
    canCreateMerchantsDirect: "สามารถสร้างร้านค้า (โดยตรง)",
    allowCreatingMerchantAccountsDirectly: "อนุญาตให้สร้างบัญชีร้านค้าโดยตรง",
    canEditFeeStructure: "สามารถแก้ไขโครงสร้างค่าธรรมเนียม",
    allowModifyingFeeSettings: "อนุญาตให้แก้ไขการตั้งค่าค่าธรรมเนียม",
    canViewWebhookLogs: "สามารถดูบันทึกเว็บฮุค",
    accessToRawWebhookData: "เข้าถึงข้อมูลเว็บฮุคดิบ",
    canRegenerateApiKeys: "สามารถสร้างกุญแจ API ใหม่",
    allowRegeneratingApiCredentials: "อนุญาตให้สร้างข้อมูลรับรอง API ใหม่",
    apiCredentials: "ข้อมูลรับรอง API",
    shareCredentialsSecurely: "แชร์ข้อมูลรับรองเหล่านี้อย่างปลอดภัยกับพันธมิตรเอนจิ้น",
    keepCredentialsSecure: "เก็บข้อมูลรับรองเหล่านี้ไว้อย่างปลอดภัย พวกเขาให้การเข้าถึง API เต็มรูปแบบ",
    updateEngine: "อัปเดตเอนจิ้น",
    saving: "กำลังบันทึก...",
    pleaseFillAllRequiredFields: "กรุณากรอกข้อมูลในช่องที่จำเป็นทั้งหมด",
    engineCodePlaceholder: "ENG001",
    businessNamePlaceholder: "Gaming Partners Ltd",
    loginEmailPlaceholder: "admin@engine.com",

    // Resellers
    resellerPartners: "พันธมิตรตัวแทนจำหน่ายที่ลงทะเบียน",
    searchResellers: "ค้นหาตัวแทนจำหน่าย...",
    createReseller: "สร้างตัวแทนจำหน่าย",
    reseller: "ตัวแทนจำหน่าย",
    monthlyVolume: "ปริมาณรายเดือน",
    suspendReseller: "ระงับตัวแทนจำหน่าย",
    activateReseller: "เปิดใช้งานตัวแทนจำหน่าย",
    merchantsWillBeAffected: "พวกเขาและร้านค้าของพวกเขาจะไม่สามารถดำเนินการธุรกรรมได้",
    deleteReseller: "ลบตัวแทนจำหน่าย",
    merchantsBecomeOrphaned: "ร้านค้าทั้งหมดภายใต้ตัวแทนจำหน่ายนี้จะกลายเป็นร้านค้าไร้เจ้าของ",
    editReseller: "แก้ไขตัวแทนจำหน่าย",
    createNewReseller: "สร้างตัวแทนจำหน่ายใหม่",
    updateResellerDetails: "อัปเดตรายละเอียดและสิทธิ์ตัวแทนจำหน่าย",
    setUpNewResellerPartner: "ตั้งค่าพันธมิตรตัวแทนจำหน่ายใหม่",
    resellerProfileAndContact: "โปรไฟล์และรายละเอียดการติดต่อตัวแทนจำหน่าย",
    resellerCode: "รหัสตัวแทนจำหน่าย",
    parentType: "ประเภทหน่วยงานหลัก",
    whoManagesThisReseller: "ใครจัดการตัวแทนจำหน่ายนี้",
    emailCannotBeChangedAfterCreation: "ไม่สามารถเปลี่ยนอีเมลได้หลังจากสร้างแล้ว",
    newPasswordLeaveBlank: "รหัสผ่านใหม่ (เว้นว่างเพื่อเก็บรหัสผ่านปัจจุบัน)",
    configureResellerFeeStructure: "ตั้งค่าโครงสร้างค่าธรรมเนียมและขีดจำกัดตัวแทนจำหน่าย",
    depositFeeThb: "ค่าธรรมเนียมฝาก (฿)",
    withdrawalFeeThb: "ค่าธรรมเนียมถอน (฿)",
    percentageFeePercent: "ค่าธรรมเนียมเปอร์เซ็นต์ (%)",
    commissionRatePercent: "อัตราค่าคอมมิชชั่น (%)",
    maxMerchantsLabel: "ร้านค้าสูงสุด",
    maximumMerchantsResellerCanCreate: "จำนวนร้านค้าสูงสุดที่ตัวแทนจำหน่ายนี้สามารถสร้างได้",
    resellerCommissionOnTransactions: "ค่าคอมมิชชั่นตัวแทนจำหน่ายจากธุรกรรม",
    thisCommissionRateSetBy: "อัตราค่าคอมมิชชั่นนี้ถูกกำหนดโดย {name} และไม่สามารถเปลี่ยนแปลงได้",
    allowedProviders: "ผู้ให้บริการที่อนุญาต",
    selectWhichPaymentProviders: "เลือกผู้ให้บริการชำระเงินที่ตัวแทนจำหน่ายนี้สามารถใช้ได้",
    routingOverride: "การกำหนดเส้นทางแทนที่",
    configureRoutingBehavior: "ตั้งค่าพฤติกรรมการกำหนดเส้นทางสำหรับตัวแทนจำหน่ายนี้",
    failoverEnabled: "เปิดใช้งานการสำรอง",
    automaticallyTryNextProvider: "ลองผู้ให้บริการถัดไปอัตโนมัติเมื่อล้มเหลว",
    priorityProvidersInOrder: "ผู้ให้บริการลำดับความสำคัญ (ตามลำดับ)",
    featurePermissions: "สิทธิ์ฟีเจอร์",
    controlWhatActionsReseller: "ควบคุมการดำเนินการที่ตัวแทนจำหน่ายสามารถทำได้",
    visibleDashboardPages: "หน้าดashboard ที่มองเห็นได้",
    controlWhichPagesReseller: "ควบคุมหน้าที่ตัวแทนจำหน่ายสามารถเข้าถึงได้",
    resellerApiKeysForIntegration: "กุญแจ API ตัวแทนจำหน่ายสำหรับการผสานรวม",
    apiKeysAutoGenerated: "กุญแจ API ถูกสร้างอัตโนมัติ แชร์อย่างปลอดภัยกับตัวแทนจำหน่าย",
    updateReseller: "อัปเดตตัวแทนจำหน่าย",
    createResellerButton: "สร้างตัวแทนจำหน่าย",

    // Global Transactions
    globalTransactions: "ธุรกรรมทั้งหมด",
    transactionsAcrossEntities: "ธุรกรรมทั้งหมดในทุกหน่วยงาน",
    filters: "ตัวกรอง",
    clearAll: "ล้างทั้งหมด",
    searchIdRefMerchant: "ค้นหา ID, อ้างอิง, ร้านค้า...",
    allEngines: "เอนจิ้นทั้งหมด",
    allResellers: "ตัวแทนจำหน่ายทั้งหมด",
    allProviders: "ผู้ให้บริการทั้งหมด",
    allMerchants: "ร้านค้าทั้งหมด",
    activeFilters: "ใช้งาน",
    errorLoadingTransactions: "เกิดข้อผิดพลาดในการโหลดธุรกรรม",
    pleaseCheckConnection: "กรุณาตรวจสอบการเชื่อมต่อและลองอีกครั้ง",
    fromDate: "จากวันที่",
    toDate: "ถึงวันที่",

    // Servers
    serverInfrastructure: "โครงสร้างพื้นฐานเซิร์ฟเวอร์",
    realTimeMonitoring: "การตรวจสอบแบบเรียลไทม์และสถานะสุขภาพ",
    last1h: "1 ชม. ล่าสุด",
    last6h: "6 ชม. ล่าสุด",
    last24h: "24 ชม. ล่าสุด",
    last7d: "7 วันล่าสุด",
    healthy: "ปกติ",
    warnings: "คำเตือน",
    avgCpu: "CPU เฉลี่ย",
    avgMemory: "หน่วยความจำเฉลี่ย",
    requestsPerMin: "คำขอ/นาที",
    avgLatency: "เวลาตอบสนองเฉลี่ย",
    overview: "ภาพรวม",
    performance: "ประสิทธิภาพ",
    network: "เครือข่าย",
    cpuUsage: "การใช้ CPU (24 ชม.)",
    memoryUsage: "การใช้หน่วยความจำ (24 ชม.)",
    responseLatency: "เวลาตอบสนอง (24 ชม.)",
    networkTraffic: "ปริมาณการใช้งานเครือข่ายตามเซิร์ฟเวอร์",
    cpu: "CPU",
    memory: "หน่วยความจำ",
    disk: "ดิสก์",
    latency: "เวลาตอบสนอง",
    uptime: "เวลาทำงาน",
    reqPerMin: "คำขอ/นาที",
    viewLogs: "ดูบันทึก",
    requestsPerMin24h: "คำขอ/นาที (24 ชม.)",
    inbound: "ขาเข้า",
    outbound: "ขาออก",
    disabled: "ปิดใช้งาน",
    warning: "คำเตือน",
    critical: "วิกฤต",
    offline: "ออฟไลน์",
    serverLogs: "บันทึกเซิร์ฟเวอร์",
    serverLogsTitle: "บันทึกเซิร์ฟเวอร์ - {name}",
    viewServerLogsDescription: "ดูบันทึกเซิร์ฟเวอร์รวมถึงเวลาเริ่มต้น การเชื่อมต่อฐานข้อมูล การใช้หน่วยความจำ และสถานะคำขอ",
    loadingLogs: "กำลังโหลดบันทึก...",
    serverStarted: "เซิร์ฟเวอร์เริ่มทำงาน",
    databaseConnectionEstablished: "เชื่อมต่อฐานข้อมูลแล้ว",
    currentMemoryUsage: "การใช้หน่วยความจำปัจจุบัน",
    totalLogEntries: "รายการบันทึกทั้งหมด",
    filterByStatus: "กรองตามสถานะ",
    allStatuses: "สถานะทั้งหมด",
    statusUp: "ทำงาน",
    statusLow: "ต่ำ",
    statusCritical: "วิกฤต",
    statusDown: "หยุดทำงาน",
    recentLogEntries: "รายการบันทึกล่าสุด",
    showingEntries: "แสดง {filtered} จาก {total} รายการ",
    showingEntriesStatusChanges: "แสดง {filtered} จาก {total} รายการ (เฉพาะการเปลี่ยนแปลงสถานะ)",
    latencyLabel: "เวลาตอบสนอง",
    noLogsAvailable: "ไม่มีบันทึก",
    connectionFailed: "การเชื่อมต่อล้มเหลว - คำขออาจถูกคิวหรือล้มเหลว",
    criticalLatencyDetected: "ตรวจพบเวลาตอบสนองวิกฤต - ต้องให้ความสนใจทันที",
    highLatencyDetected: "ตรวจพบเวลาตอบสนองสูง - ประสิทธิภาพอาจลดลง",
    apiGateway: "API Gateway",
    paymentProcessor: "ตัวประมวลผลการชำระเงิน",
    databasePrimary: "ฐานข้อมูลหลัก",
    databaseReplica: "ฐานข้อมูลสำเนา",
    webhookHandler: "ตัวจัดการ Webhook",
    queueProcessor: "ตัวประมวลผลคิว",
    loadBalancer: "ตัวปรับสมดุลโหลด",
    redisCache: "Redis Cache",
    singapore: "สิงคโปร์",
    bangkok: "กรุงเทพฯ",
    na: "ไม่มีข้อมูล",
    failedToFetchLogs: "ไม่สามารถดึงบันทึกได้",

    // Merchant Panel
    merchantPortal: "พอร์ทัลร้านค้า",
    navigation: "การนำทาง",
    loggedInAs: "เข้าสู่ระบบในฐานะ",

    // Merchant Dashboard
    welcome: "ยินดีต้อนรับกลับ",

    // Merchant Settings
    apiKeys: "กุญแจ API",
    yourAuthCredentials: "ข้อมูลรับรองการยืนยันตัวตนสำหรับการเข้าถึง API",
    apiKeyPublic: "กุญแจ API (สาธารณะ)",
    secretKey: "กุญแจลับ",
    regenerateKeys: "สร้างกุญแจใหม่",
    invalidateCurrentKeys: "จะทำให้กุญแจปัจจุบันใช้ไม่ได้",
    regenerate: "สร้างใหม่",
    regenerateApiKeys: "สร้างกุญแจ API ใหม่?",
    invalidateImmediately: "การดำเนินการนี้จะทำให้กุญแจ API ปัจจุบันใช้ไม่ได้ทันที การผสานรวมที่มีอยู่ทั้งหมดที่ใช้กุญแจเหล่านี้จะหยุดทำงานจนกว่าจะอัปเดต",
    callbackUrls: "URL การเรียกกลับ",
    whereWeSendNotifications: "ที่เราส่งการแจ้งเตือนการชำระเงิน",
    callbackUrl: "URL การเรียกกลับ",
    wePostToEndpoint: "เรา POST การอัปเดตธุรกรรมไปยังจุดปลายนี้",
    webhookUrl: "URL เว็บฮุค (ไม่บังคับ)",
    additionalEventNotifications: "สำหรับการแจ้งเตือนเหตุการณ์เพิ่มเติม",
    saveUrls: "บันทึก URL",
    merchantProfile: "โปรไฟล์ร้านค้า",
    yourBusinessInfo: "ข้อมูลธุรกิจของคุณ",
    businessName: "ชื่อธุรกิจ",
    emailAddress: "ที่อยู่อีเมล",
    supportContact: "ติดต่อฝ่ายสนับสนุน",
    saveProfile: "บันทึกโปรไฟล์",

    // Engine Panel
    enginePanel: "แผงควบคุมเอนจิ้น",

    // Reseller Panel
    resellerPanel: "แผงควบคุมตัวแทนจำหน่าย",

    // Common form labels
    name: "ชื่อ",
    email: "อีเมล",
    phone: "โทรศัพท์",
    contactName: "ชื่อผู้ติดต่อ",
    contactEmail: "อีเมลติดต่อ",
    contactPhone: "โทรศัพท์ติดต่อ",
    notes: "หมายเหตุ",

    // API related
    apiCredentials: "ข้อมูลรับรอง API",
    keepSecretConfidential: "เก็บกุญแจลับของคุณไว้เป็นความลับ",
    securityNotice: "ประกาศความปลอดภัย",
    neverExposeSecretKey: "อย่าเปิดเผยในโค้ดฝั่งไคลเอนต์หรือแชร์สาธารณะ",
    regenerateAllKeys: "สร้างกุญแจทั้งหมดใหม่",
    invalidateCurrentKeysWarning: "สร้างกุญแจ API ใหม่ กุญแจปัจจุบันจะหยุดทำงานทันที",

    // Withdrawals
    manualPayout: "การจ่ายเงินด้วยตนเอง",
    newWithdrawal: "ถอนเงินใหม่",
    history: "ประวัติ",
    withdrawalRequestSubmittedSuccessfully: "ส่งคำขอถอนเงินสำเร็จแล้ว!",
    noWithdrawalsFound: "ไม่พบการถอนเงิน",
    bank: "ธนาคาร",
    bankAccountNumber: "หมายเลขบัญชีธนาคาร",
    accountHolderName: "ชื่อเจ้าของบัญชี",
    enterAccountHolderName: "กรอกชื่อเจ้าของบัญชี",
    orderId: "รหัสคำสั่งซื้อ",
    customerId: "รหัสลูกค้า",
    optionalCustomerRef: "อ้างอิงลูกค้า (ไม่บังคับ)",
    createPayout: "สร้างการจ่ายเงิน",

    // Dialogs
    areYouSure: "คุณแน่ใจหรือไม่?",

    // Infrastructure
    infrastructure: "โครงสร้างพื้นฐาน",
    apiGateway: "เกตเวย์ API",
    database: "ฐานข้อมูล",
    queue: "คิว",

    // Panel specific
    adminPanel: "แผงควบคุมผู้ดูแล",

    // Additional
    total: "ทั้งหมด",
    successful: "สำเร็จ",
    completed: "เสร็จสิ้น",

    // Transaction fields
    transaction: "ธุรกรรม",
    method: "วิธีการ",
    ref: "อ้างอิง",
    paymentId: "รหัสการชำระเงิน",
    clientName: "ชื่อลูกค้า",
    clientBankAccount: "บัญชีธนาคารลูกค้า",

    // Filters
    allTypes: "ทุกประเภท",
    allMerchants: "ร้านค้าทั้งหมด",
    dateRange: "ช่วงวันที่",
    clearFilters: "ล้างตัวกรอง",
    exportCsv: "ส่งออก CSV",
    searchHeaderPlaceholder: "ค้นหาธุรกรรม, ร้านค้า...",

    // Stats
    vsLastPeriod: "เทียบกับช่วงก่อน",
    live: "สด",
    noTransactionsYet: "ยังไม่มีธุรกรรม",
    noProvidersConfigured: "ยังไม่มีการตั้งค่าผู้ให้บริการ",
    noProvidersFound: "ไม่พบผู้ให้บริการ",
    avgResponse: "การตอบสนองเฉลี่ย",

    // Routing Rule Form fields
    ruleName: "ชื่อกฎ",

    // Withdrawal form
    selectMerchant: "เลือกร้านค้า",
    selectBank: "เลือกธนาคาร",

    // Priority badge text
    priorityText: "ลำดับความสำคัญ",
    viewOnlyBadge: "ดู",

    // Additional transaction/common
    retry: "ลองใหม่",
    noTransactionsFound: "ไม่พบธุรกรรม",
    noDeposits: "ไม่มีการฝากเงิน",

    // Transaction Detail
    transactionDetails: "รายละเอียดธุรกรรม",
    transactionInfo: "ข้อมูลธุรกรรม",
    processingInfo: "ข้อมูลการประมวลผล",
    customerInfo: "ข้อมูลลูกค้า",
    bankDetails: "รายละเอียดธนาคาร",
    callbackStatus: "สถานะ Callback",
    timestamps: "เวลา",
    retryTransaction: "ลองธุรกรรมใหม่",
    resendCallback: "ส่ง Callback ใหม่",
    routingAttempts: "จำนวนครั้งการกำหนดเส้นทาง",
    attempts: "ครั้ง",
    lastAttempt: "ครั้งล่าสุด",
    createdAt: "สร้างเมื่อ",
    processedAt: "ประมวลผลเมื่อ",
    completedAt: "เสร็จสิ้นเมื่อ",
    updatedAt: "อัปเดตเมื่อ",
    currency: "สกุลเงิน",
    feeAmount: "ค่าธรรมเนียม",
    providerRef: "อ้างอิงผู้ให้บริการ",
    account: "บัญชี",
    nA: "ไม่มี",
    errorCode: "รหัสข้อผิดพลาด",
    qrCode: "รหัส QR",
    qrPayment: "การชำระเงิน QR",
    qrPaymentInfo: "ข้อมูลการชำระเงิน QR",
    qrCodeInfo: "ข้อมูลรหัส QR",
    qrCodeDetails: "รายละเอียดรหัส QR",
    qrCodePayment: "การชำระเงินด้วยรหัส QR",
    expiryInfo: "ข้อมูลการหมดอายุ",
    expiresAt: "หมดอายุเมื่อ",
    viewQrCode: "ดูรหัส QR",
    expires: "หมดอายุ",
    manualStatusUpdate: "อัปเดตสถานะด้วยตนเอง",
    updateStatus: "อัปเดตสถานะ",
    newStatus: "สถานะใหม่",
    adminNoteOptional: "หมายเหตุผู้ดูแลระบบ (ไม่บังคับ)",
    adminNote: "หมายเหตุผู้ดูแลระบบ",
    reasonForStatusChange: "เหตุผลในการเปลี่ยนสถานะ...",
    characters: "ตัวอักษร",
    updating: "กำลังอัปเดต...",
    statusUpdated: "อัปเดตสถานะแล้ว",
    transactionStatusChangedTo: "สถานะธุรกรรมเปลี่ยนเป็น",
    updateFailed: "อัปเดตล้มเหลว",
    failedToUpdateTransactionStatus: "ไม่สามารถอัปเดตสถานะธุรกรรมได้",
    webhookInfo: "ข้อมูลเว็บฮุค",
    webhook: "เว็บฮุค",
    url: "URL",
    rawWebhookData: "ข้อมูล Webhook ดิบ",
    updated: "อัปเดตเมื่อ",
    ip: "IP",
    createdLabel: "สร้างแล้ว",
    paymentMethod: "วิธีการชำระเงิน",
    hierarchy: "ลำดับชั้น",
    delivered: "ส่งแล้ว",
    callback: "การเรียกกลับ",

    // Withdrawal Form
    bankTransfer: "โอนเงินผ่านธนาคาร",
    usdtCrypto: "USDT คริปโต",
    selectBankLabel: "เลือกธนาคาร",
    chooseYourBank: "เลือกธนาคารของคุณ",
    accountNumber: "หมายเลขบัญชี",
    enterAccountNumber: "กรอกหมายเลขบัญชีธนาคารของคุณ",
    enterAccountHolderName: "กรอกชื่อเจ้าของบัญชี",
    amountThb: "จำนวนเงิน (บาท)",
    minimumWithdrawal: "ถอนขั้นต่ำ",
    withdrawToBank: "ถอนเงินเข้าธนาคาร",
    networkLabel: "เครือข่าย",
    walletAddress: "ที่อยู่กระเป๋าเงิน",
    enterWalletAddress: "กรอกที่อยู่ {network} USDT ของคุณ",
    amountUsdt: "จำนวนเงิน (USDT)",
    thbEquivalent: "เทียบเท่าบาท",
    exchangeRate: "อัตราแลกเปลี่ยน",
    withdrawUsdt: "ถอน USDT",
    withdrawalSummary: "สรุปการถอนเงิน",
    totalWithdrawn: "ยอดถอนทั้งหมด",
    requests: "คำขอ",
    processingTime: "ระยะเวลาดำเนินการ",
    hours: "ชั่วโมง",
    mins: "นาที",
    needHelp: "ต้องการความช่วยเหลือ?",
    contactSupport: "ติดต่อฝ่ายสนับสนุนหากการถอนเงินใช้เวลานานกว่าที่คาดไว้",
    walletWarning: "กรุณาตรวจสอบให้แน่ใจว่าที่อยู่กระเป๋าเงินถูกต้องและรองรับ",
    incorrectAddressWarning: "ที่อยู่ไม่ถูกต้องอาจทำให้สูญเสียเงินทุนถาวร",

    // Merchant Form
    editMerchant: "แก้ไขร้านค้า",
    addNewMerchant: "เพิ่มร้านค้าใหม่",
    merchantName: "ชื่อร้านค้า",
    merchantCode: "รหัสร้านค้า",
    parentType: "ประเภทหน่วยงานหลัก",
    directAdmin: "ตรง (ผู้ดูแล)",
    enableDeposits: "เปิดใช้งานการฝากเงิน",
    allowDepositTransactions: "อนุญาตธุรกรรมฝากเงิน",
    enableWithdrawals: "เปิดใช้งานการถอนเงิน",
    allowWithdrawalTransactions: "อนุญาตธุรกรรมถอนเงิน",
    apiSettings: "การตั้งค่า API",
    limitsFees: "ขีดจำกัด & ค่าธรรมเนียม",
    contact: "ติดต่อ",
    securityNote: "กุญแจ API จะแสดงเพียงครั้งเดียว กรุณาเก็บรักษาไว้อย่างปลอดภัย",
    ipWhitelist: "รายการ IP ที่อนุญาต",
    noIpsWhitelisted: "ไม่มี IP ที่อยู่ใน whitelist",
    rateLimit: "ขีดจำกัดคำขอ (คำขอ/นาที)",
    minDeposit: "ฝากขั้นต่ำ",
    maxDeposit: "ฝากสูงสุด",
    minWithdrawal: "ถอนขั้นต่ำ",
    maxWithdrawal: "ถอนสูงสุด",
    dailyDepositLimit: "ขีดจำกัดฝากรายวัน",
    dailyWithdrawalLimit: "ขีดจำกัดถอนรายวัน",
    feeType: "ประเภทค่าธรรมเนียม",
    feeValue: "ค่าธรรมเนียม",
    percentage: "เปอร์เซ็นต์",
    fixed: "คงที่",
    tiered: "แบบขั้น",
    settlementPeriod: "ระยะเวลาชำระบัญชี",
    sameDay: "วันเดียวกัน",
    nextDay: "วันถัดไป",
    weekly: "รายสัปดาห์",
    updateMerchant: "อัปเดตร้านค้า",
    createMerchantBtn: "สร้างร้านค้า",
    merchantInfo: "ข้อมูลร้านค้า",
    emailCannotBeChanged: "ไม่สามารถเปลี่ยนอีเมลได้",
    password: "รหัสผ่าน",
    changePassword: "เปลี่ยนรหัสผ่าน",
    leaveBlankToKeepPassword: "เว้นว่างไว้เพื่อเก็บรหัสผ่านปัจจุบัน",
    changePasswordDescription: "กรอกรหัสผ่านใหม่เพื่อเปลี่ยน หรือเว้นว่างไว้เพื่อเก็บรหัสผ่านปัจจุบัน",
    selectEngine: "เลือก Engine",
    selectReseller: "เลือก Reseller",
    whoManagesMerchant: "ใครจัดการร้านค้านี้",
    engine: "เอนจิ้น",
    reseller: "ตัวแทนจำหน่าย",
    notes: "หมายเหตุ",
    internalNotes: "หมายเหตุภายใน...",
    security: "ความปลอดภัย",
    apiKeysShownOnce: "กุญแจ API จะแสดงเพียงครั้งเดียว กรุณาเก็บรักษาไว้อย่างปลอดภัย",
    regenerate: "สร้างใหม่",
    secretKey: "กุญแจลับ",
    callbackUrl: "URL การเรียกกลับ",
    webhookUrl: "URL เว็บฮุค",
    ipWhitelistPlaceholder: "เช่น 192.168.1.1",
    minDepositThb: "ฝากขั้นต่ำ (บาท)",
    maxDepositThb: "ฝากสูงสุด (บาท)",
    minWithdrawalThb: "ถอนขั้นต่ำ (บาท)",
    maxWithdrawalThb: "ถอนสูงสุด (บาท)",
    dailyDepositLimitThb: "ขีดจำกัดฝากรายวัน (บาท)",
    dailyWithdrawalLimitThb: "ขีดจำกัดถอนรายวัน (บาท)",
    feeValueLabel: "ค่าธรรมเนียม",
    contactName: "ชื่อผู้ติดต่อ",
    contactPhone: "เบอร์โทรติดต่อ",
    backButton: "← ย้อนกลับ",
    nextButton: "ถัดไป →",
    merchantNamePlaceholder: "เช่น Gaming Site A",
    merchantCodePlaceholder: "เช่น MGC001",
    emailPlaceholder: "merchant@example.com",
    passwordPlaceholder: "กรอกรหัสผ่าน",
    callbackUrlPlaceholder: "https://your-site.com/api/payment/callback",
    webhookUrlPlaceholder: "https://your-site.com/api/payment/webhook",
    contactNamePlaceholder: "John Doe",
    contactEmailPlaceholder: "john@example.com",
    contactPhonePlaceholder: "+66 XXX XXX XXXX",
    t0SameDay: "T+0 (วันเดียวกัน)",
    t1NextDay: "T+1 (วันถัดไป)",
    t3: "T+3",
    t7Weekly: "T+7 (รายสัปดาห์)",
    apiKeyPublic: "กุญแจ API (สาธารณะ)",
    createMerchantUnderReseller: "สร้างร้านค้าใหม่ภายใต้บัญชีตัวแทนจำหน่ายของคุณ",
    commissionGivenToYouBy: "นี่คืออัตราค่าคอมมิชชั่นที่ {name} กำหนดให้คุณ คุณสามารถกำหนดค่าคอมมิชชั่นให้ร้านค้านี้ได้",
    feeValueSetToBy: "ค่าธรรมเนียมนี้ถูกกำหนดไว้ที่ {value}% (อัตราค่าคอมมิชชั่นที่ {name} กำหนดให้คุณ) และไม่สามารถเปลี่ยนแปลงได้",

    // Provider Form
    editProvider: "แก้ไขผู้ให้บริการ",
    addNewProvider: "เพิ่มผู้ให้บริการใหม่",
    providerNameRequired: "ชื่อผู้ให้บริการ *",
    providerCodeRequired: "รหัสผู้ให้บริการ *",
    providerNamePlaceholder: "เช่น SCB",
    providerCodePlaceholder: "เช่น SCB",
    providerTypeEwallet: "อีวอลเล็ต",
    providerTypeQr: "ชำระเงิน QR",
    providerTypeCard: "บัตร",
    providerName: "ชื่อผู้ให้บริการ",
    providerCode: "รหัสผู้ให้บริการ",
    supportsDeposits: "รองรับการฝากเงิน",
    processDepositTransactions: "ประมวลผลธุรกรรมฝากเงิน",
    supportsWithdrawals: "รองรับการถอนเงิน",
    processWithdrawalTransactions: "ประมวลผลธุรกรรมถอนเงิน",
    supportsQr: "รองรับการชำระเงิน QR",
    generatePromptPayQr: "สร้าง QR Code PromptPay",
    logoUrl: "URL โลโก้",
    apiConfig: "การตั้งค่า API",
    routingLimits: "การกำหนดเส้นทาง & ขีดจำกัด",
    apiEndpoint: "จุดปลาย API",
    apiEndpointPlaceholder: "https://api.provider.com/v1",
    apiKey: "กุญแจ API",
    apiSecret: "กุญแจลับ API",
    webhookSecret: "กุญแจลับ Webhook",
    providerIpWhitelist: "รายการ IP ผู้ให้บริการที่อนุญาต (สำหรับ webhooks)",
    priorityHighest: "ลำดับความสำคัญ (1 = สูงสุด)",
    weightLoadBalancing: "น้ำหนัก (การกระจายโหลด)",
    dailyLimit: "ขีดจำกัดรายวัน",
    minAmountThb: "จำนวนขั้นต่ำ (บาท)",
    maxAmountThb: "จำนวนสูงสุด (บาท)",
    dailyLimitThb: "ขีดจำกัดรายวัน (บาท)",
    feePercentage: "เปอร์เซ็นต์ค่าธรรมเนียม",
    settlementTimeLabel: "ระยะเวลาชำระบัญชี",
    instant: "ทันที",
    oneHour: "1 ชั่วโมง",
    updateProvider: "อัปเดตผู้ให้บริการ",
    addProviderBtn: "เพิ่มผู้ให้บริการ",

    // Common
    to: "ถึง",

    // Engine Panel specific
    welcomeBack: "ยินดีต้อนรับกลับ",
    totalVolume: "ปริมาณรวม",
    volumeOverviewTitle: "ภาพรวมปริมาณ",
    last7DaysVolume: "ปริมาณธุรกรรม 7 วันล่าสุด",
    recent: "ล่าสุด",
    noMerchantsFound: "ไม่พบร้านค้า",
    noMerchantsMatchSearch: "ไม่พบร้านค้าที่ตรงกับการค้นหา",
    todaysVolume: "ปริมาณวันนี้",
    volumeTrend7Days: "แนวโน้มปริมาณ (7 วัน)",
    apiAndSettings: "API และการตั้งค่า",
    avgDeposit: "ยอดฝากเฉลี่ย",
    searchMerchantsPlaceholder: "ค้นหาร้านค้า...",
    merchantsRegisteredCount: "ร้านค้าที่ลงทะเบียน",
    partnersCount: "พันธมิตร",
    addReseller: "เพิ่มตัวแทนจำหน่าย",
    noResellers: "ไม่มีตัวแทนจำหน่าย",
    viewOnly: "ดูอย่างเดียว",
    paymentProviderStatus: "สถานะผู้ให้บริการชำระเงิน (จัดการโดยผู้ดูแล)",
    searchProviders: "ค้นหาผู้ให้บริการ...",
    configureProviderSelection: "ตั้งค่าการเลือกผู้ให้บริการและตรรกะการสำรอง",
    howRoutingWorks: "การกำหนดเส้นทางทำงานอย่างไร",
    routingExplanation: "กฎจะถูกประเมินตามลำดับความสำคัญ กฎแรกที่ตรงกันจะกำหนดว่าผู้ให้บริการใดจะจัดการธุรกรรม หากผู้ให้บริการล้มเหลว ระบบจะสำรองไปยังรายถัดไปโดยอัตโนมัติ",
    noRoutingRules: "ยังไม่มีการตั้งค่ากฎการกำหนดเส้นทาง",
    createFirstRoutingRule: "สร้างกฎการกำหนดเส้นทางแรกของคุณเพื่อเริ่มนำทางธุรกรรม",
    failover: "การสำรอง",
    logs: "บันทึก",
    noLogs: "ไม่มีบันทึก",
    payload: "ข้อมูล",
    noSettlementsFound: "ไม่มีการชำระบัญชี",
    manageProfile: "จัดการโปรไฟล์ตัวแทนจำหน่ายและข้อมูลรับรอง API",
    resellerProfile: "โปรไฟล์ตัวแทนจำหน่าย",
    yourBusinessInfo: "ข้อมูลธุรกิจของคุณ",
    resellerCode: "รหัสตัวแทนจำหน่าย",
    supportEmail: "อีเมลสนับสนุน",
    apiCredentialsTitle: "ข้อมูลรับรอง API",
    yourApiKeysIntegration: "กุญแจ API สำหรับการผสานรวม",
    regenerateApiKeysTitle: "สร้างกุญแจ API ใหม่",
    regenerateApiKeysDesc: "สร้างกุญแจ API ใหม่ กุญแจปัจจุบันจะหยุดทำงานทันที",
    regenerateApiKeysConfirm: "นี่จะทำให้กุญแจ API ปัจจุบันใช้ไม่ได้ทันที การผสานรวมทั้งหมดที่ใช้กุญแจเก่าจะหยุดทำงาน กรุณาอัปเดตการตั้งค่าเซิร์ฟเวอร์ด้วยกุญแจใหม่",
    regenerateKeys: "สร้างกุญแจใหม่",
    requestPayoutsViaBankUsdt: "ขอเบิกเงินผ่านธนาคารหรือ USDT",
    clear: "ล้าง",
    allowedProviders: "ผู้ให้บริการที่อนุญาต",
    feeConfiguration: "การตั้งค่าค่าธรรมเนียม",
    percentageFeeLabel: "ค่าธรรมเนียมเปอร์เซ็นต์ (%)",
    feesAndCommission: "ค่าธรรมเนียม & ค่าคอมมิชชั่น",
    commissionRatesAndFeeInfo: "อัตราค่าคอมมิชชั่นและข้อมูลค่าธรรมเนียม",
    givenBy: "กำหนดโดย",
    depositCommission: "ค่าคอมมิชชั่นฝากเงิน",
    withdrawalCommission: "ค่าคอมมิชชั่นถอนเงิน",
    yourCommissionRates: "อัตราค่าคอมมิชชั่นของคุณ",
    ratesAssignedToYou: "นี่คืออัตราที่กำหนดให้คุณ",
    suspendMerchantConfirm: "คุณแน่ใจหรือไม่ที่จะระงับ",
    willNotProcessTransactions: "พวกเขาจะไม่สามารถดำเนินการธุรกรรมได้",
    activateMerchantConfirm: "คุณแน่ใจหรือไม่ที่จะเปิดใช้งาน",
    suspendMerchantTitle: "ระงับร้านค้า",
    activateMerchantTitle: "เปิดใช้งานร้านค้า",
    createNewMerchant: "สร้างร้านค้าใหม่",
    businessNameLabel: "ชื่อธุรกิจ",
    emailLabel: "อีเมล",
    callbackUrlLabel: "URL การเรียกกลับ",

    // Transaction Detail Sheet
    statusTimeline: "ไทม์ไลน์สถานะ",
    createdLabel: "สร้างแล้ว",
    processingLabel: "กำลังดำเนินการ",
    completedLabel: "เสร็จสิ้น",
    failedLabel: "ล้มเหลว",
    error: "ข้อผิดพลาด",
    reference: "อ้างอิง",

    // Additional Merchant Panel
    newDeposit: "ฝากเงินใหม่",
    generateQrDeposit: "สร้าง QR ฝากเงิน",
    createPromptPayQr: "สร้าง QR Code PromptPay สำหรับลูกค้า",
    howItWorks: "วิธีการทำงาน",
    enterDepositAmount: "กรอกจำนวนเงินฝาก",
    generateQr: "สร้าง QR Code",
    customerScansQr: "ลูกค้าสแกน QR",
    paymentProcessed: "ดำเนินการชำระเงิน",
    waitingForPayment: "รอการชำระเงิน",
    statusAutoRefresh: "สถานะจะรีเฟรชอัตโนมัติ",
    paymentReceived: "ได้รับการชำระเงินแล้ว",
    qrCodeExpired: "QR Code หมดอายุ",
    generateNewQr: "สร้าง QR ใหม่",

    // Additional Engine specific
    successfulDeposits: "ฝากเงินสำเร็จ",
    failedDeposits: "ฝากเงินล้มเหลว",

    // Merchant Deposit
    generateQrDeposit: "สร้าง QR ฝากเงิน",
    createPromptPayQr: "สร้าง QR Code PromptPay สำหรับลูกค้า",
    newDeposit: "ฝากเงินใหม่",
    maintenance: "ปรับปรุงระบบ",
    degraded: "ลดประสิทธิภาพ",
    totalSettled: "ชำระบัญชีทั้งหมด",
    pendingSettlement: "รอชำระบัญชี",
    totalRecords: "บันทึกทั้งหมด",
    transactionVolume: "ปริมาณธุรกรรม",
    last7DaysPerformance: "ผลงาน 7 วันล่าสุด",
    transactionPerformance: "ประสิทธิภาพธุรกรรม",
    welcomeBackOverview: "ยินดีต้อนรับกลับ! นี่คือภาพรวมของคุณ",
    noEmail: "ไม่มีอีเมล",

    // Additional missing translations
    searchByWebhookId: "ค้นหาด้วย webhook ID, transaction ID, แหล่งที่มา...",
    selectEngine: "เลือกเอนจิ้น",
    selectPanelType: "เลือกประเภทแผงควบคุม",
    enterPassword: "กรอกรหัสผ่าน",
    enterNewPassword: "กรอกรหัสผ่านใหม่",
    searchRoles: "ค้นหาบทบาท...",
    searchStaff: "ค้นหาพนักงาน...",
    searchBySettlementId: "ค้นหาด้วยรหัสการชำระบัญชี...",
    searchByTransactionId: "ค้นหาด้วยรหัสธุรกรรม...",
    defaultQrRoute: "เช่น Default QR Route",
    describeRole: "อธิบายว่าบทบาทนี้สามารถทำอะไรได้...",
    orderIdPlaceholder: "Order ID, Customer ID, ฯลฯ",
    amountPlaceholder: "0.00",
    accountNumberPlaceholder: "เช่น 1234567890",
    accountHolderPlaceholder: "ชื่อเต็มตามบัญชีธนาคาร",
    callbackUrlPlaceholder: "https://your-site.com/api/callback",
    webhookUrlPlaceholder: "https://your-site.com/api/webhook",
    phonePlaceholder: "+66 XXX XXX XXXX",
    searchMerchantsPlaceholder: "ค้นหาร้านค้า...",
    orderIdLabel: "รหัสคำสั่งซื้อ",
    customerIdLabel: "รหัสลูกค้า",
    orderIdPlaceholder: "กรอกรหัสคำสั่งซื้อ",
    customerIdPlaceholder: "กรอกรหัสลูกค้า",

    // Admin Roles
    staffRoles: "บทบาทพนักงาน",
    rolesCount: "บทบาท",
    customCount: "กำหนดเอง",
    systemCount: "ระบบ",
    createRole: "สร้างบทบาท",
    editRole: "แก้ไขบทบาท",
    updateRoleDetailsAndPermissions: "อัปเดตรายละเอียดและสิทธิ์ของบทบาท",
    defineNewRoleWithPermissions: "กำหนดบทบาทใหม่พร้อมสิทธิ์เฉพาะ",
    basicDetailsForRole: "รายละเอียดพื้นฐานสำหรับบทบาท",
    roleNameRequired: "ชื่อบทบาท *",
    merchantManagerPlaceholder: "เช่น ผู้จัดการร้านค้า",
    describeWhatRoleCanDo: "อธิบายว่าบทบาทนี้สามารถทำอะไรได้...",
    roleCreatedForAdminPanel: "บทบาทนี้จะถูกสร้างขึ้นสำหรับแผงควบคุมผู้ดูแลระบบ",
    selectActionsRoleCanPerform: "เลือกการดำเนินการที่บทบาทนี้สามารถทำได้",
    selectAll: "เลือกทั้งหมด",
    deselectAll: "ยกเลิกการเลือกทั้งหมด",
    noPermissionsSelected: "ไม่ได้เลือกสิทธิ์ใดๆ",
    permissionsSelected: "เลือกแล้ว {count} สิทธิ์",
    permissionSelected: "เลือกแล้ว 1 สิทธิ์",
    staffWithRoleActions: "พนักงานที่มีบทบาทนี้จะสามารถดำเนินการเหล่านี้ได้",
    updateRole: "อัปเดตบทบาท",
    provideRoleNameAndPermission: "กรุณาระบุชื่อบทบาทและเลือกอย่างน้อยหนึ่งสิทธิ์",
    roleEditingNotImplemented: "ยังไม่รองรับการแก้ไขบทบาท กรุณาลบและสร้างบทบาทใหม่",
    searchRolesPlaceholder: "ค้นหาบทบาท...",
    customRoles: "บทบาทที่กำหนดเอง",
    rolesYouveCreated: "บทบาทที่คุณสร้างขึ้น",
    role: "บทบาท",
    permissions: "สิทธิ์",
    noPermissions: "ไม่มีสิทธิ์",
    morePermissions: "เพิ่มเติม",
    systemRoles: "บทบาทระบบ",
    predefinedRolesReadOnly: "บทบาทที่กำหนดไว้ล่วงหน้า (อ่านอย่างเดียว)",
    systemBadge: "ระบบ",
    noRolesFound: "ไม่พบบทบาท",
    tryDifferentSearchTerm: "ลองใช้คำค้นหาอื่น",
    getStartedCreatingFirstRole: "เริ่มต้นด้วยการสร้างบทบาทแรกของคุณ",
    deleteRole: "ลบบทบาท",
    deleteRoleConfirmation: "คุณแน่ใจหรือไม่ที่จะลบบทบาท \"{name}\"? การดำเนินการนี้ไม่สามารถยกเลิกได้ สมาชิกพนักงานที่มีบทบาทนี้จะสูญเสียการเข้าถึง",
    systemRolesCannotBeDeleted: "ไม่สามารถลบบทบาทระบบได้",
    generalInformation: "ข้อมูลทั่วไป",
    description: "คำอธิบาย",
    panelTypeRequired: "ประเภทแผงควบคุม *",
    adminPanel: "แอดมิน",
    permissionsTitle: "สิทธิ์",
    cancel: "ยกเลิก",
    saving: "กำลังบันทึก...",
    save: "บันทึก",
    createRoleAction: "สร้างบทบาท",

    // Permissions
    "permission.merchants.view": "ดูร้านค้า",
    "permission.merchants.create": "สร้างร้านค้า",
    "permission.merchants.edit": "แก้ไขร้านค้า",
    "permission.merchants.delete": "ลบร้านค้า",
    "permission.engines.view": "ดูเอนจิ้น",
    "permission.engines.create": "สร้างเอนจิ้น",
    "permission.engines.edit": "แก้ไขเอนจิ้น",
    "permission.engines.delete": "ลบเอนจิ้น",
    "permission.resellers.view": "ดูตัวแทนจำหน่าย",
    "permission.resellers.create": "สร้างตัวแทนจำหน่าย",
    "permission.resellers.edit": "แก้ไขตัวแทนจำหน่าย",
    "permission.resellers.delete": "ลบตัวแทนจำหน่าย",
    "permission.transactions.view": "ดูธุรกรรม",
    "permission.transactions.create": "สร้างธุรกรรม",
    "permission.transactions.edit": "แก้ไขธุรกรรม",
    "permission.staff.view": "ดูพนักงาน",
    "permission.staff.create": "สร้างพนักงาน",
    "permission.staff.edit": "แก้ไขพนักงาน",
    "permission.staff.delete": "ลบพนักงาน",
    "permission.settings.view": "ดูการตั้งค่า",
    "permission.settings.edit": "แก้ไขการตั้งค่า",
    "permission.roles.view": "ดูบทบาท",
    "permission.roles.create": "สร้างบทบาท",
    "permission.roles.edit": "แก้ไขบทบาท",
    "permission.roles.delete": "ลบบทบาท",

    // System Role Names
    "role.Super Admin": "ซูเปอร์แอดมิน",
    "role.Admin Manager": "ผู้จัดการแอดมิน",
    "role.Engine Manager": "ผู้จัดการเอนจิ้น",
    "role.Reseller Manager": "ผู้จัดการตัวแทนจำหน่าย",
    "role.Merchant Manager": "ผู้จัดการร้านค้า",
    "role.Transaction Viewer": "ผู้ดูธุรกรรม",
    "role.Staff Manager": "ผู้จัดการพนักงาน",

    // System Role Descriptions
    "roleDesc.Full access to all admin functions": "เข้าถึงฟังก์ชันแอดมินทั้งหมด",
    "roleDesc.Manage admin operations": "จัดการการดำเนินงานแอดมิน",
    "roleDesc.Manage engines and their merchants": "จัดการเอนจิ้นและร้านค้าของพวกเขา",
    "roleDesc.Manage resellers and their merchants": "จัดการตัวแทนจำหน่ายและร้านค้าของพวกเขา",
    "roleDesc.Manage merchants": "จัดการร้านค้า",
    "roleDesc.View transactions only": "ดูธุรกรรมเท่านั้น",
    "roleDesc.Manage staff members": "จัดการสมาชิกพนักงาน",

    // Admin Staff Form
    editStaffMember: "แก้ไขสมาชิกพนักงาน",
    updateStaffMemberDetails: "อัปเดตรายละเอียดสมาชิกพนักงาน",
    addNewStaffMemberToOrganization: "เพิ่มสมาชิกพนักงานใหม่ในองค์กรของคุณ",
    basicDetailsForStaffMember: "รายละเอียดพื้นฐานสำหรับสมาชิกพนักงาน",
    fullName: "ชื่อเต็ม",
    emailAddress: "ที่อยู่อีเมล",
    newPasswordLeaveBlank: "รหัสผ่านใหม่ (เว้นว่างเพื่อเก็บรหัสผ่านปัจจุบัน)",
    roleAssignment: "การกำหนดบทบาท",
    assignRolesToControlAccess: "กำหนดบทบาทเพื่อควบคุมสิ่งที่สมาชิกพนักงานนี้สามารถเข้าถึงได้",
    noRolesAvailableCreateFirst: "ไม่มีบทบาทที่ใช้ได้ กรุณาสร้างบทบาทก่อน",
    updateStaffMember: "อัปเดตสมาชิกพนักงาน",
    loadingUserInformation: "กำลังโหลดข้อมูลผู้ใช้...",
    unableToGetCurrentUserId: "ไม่สามารถรับ ID ผู้ใช้ปัจจุบันได้ กรุณารีเฟรชหน้าและลองอีกครั้ง",
    pleaseFillAllRequiredFields: "กรุณากรอกข้อมูลในช่องที่จำเป็นทั้งหมด",
    pleaseProvidePassword: "กรุณาให้รหัสผ่านสำหรับสมาชิกพนักงานใหม่",
    passwordMustBeAtLeast6Characters: "รหัสผ่านต้องมีความยาวอย่างน้อย 6 ตัวอักษร",

    // Login Forms
    pleaseEnterBothEmailAndPassword: "กรุณากรอกอีเมลและรหัสผ่าน",
    loginFailed: "เข้าสู่ระบบล้มเหลว กรุณาตรวจสอบข้อมูลประจำตัวของคุณ",
    loginFailedCheckCredentials: "เข้าสู่ระบบล้มเหลว กรุณาตรวจสอบข้อมูลประจำตัวและประเภทแผงควบคุม",
    pleaseFillInAllFields: "กรุณากรอกข้อมูลในทุกช่อง",
    rememberMe: "จดจำฉัน",
    forgotPassword: "ลืมรหัสผ่าน?",
    signIn: "เข้าสู่ระบบ",
    signingIn: "กำลังเข้าสู่ระบบ...",
    signInAsStaff: "เข้าสู่ระบบในฐานะพนักงาน",
    signInAs: "เข้าสู่ระบบในฐานะ {type}",
    signInToPortal: "เข้าสู่ระบบพอร์ทัล",
    selectAccountTypeAndEnterCredentials: "เลือกประเภทบัญชีและกรอกข้อมูลประจำตัว",
    welcomeBack: "ยินดีต้อนรับกลับ",
    welcomeBackAdmin: "ยินดีต้อนรับกลับ",
    signInToAccessAdminDashboard: "เข้าสู่ระบบเพื่อเข้าถึงแดชบอร์ดผู้ดูแลระบบ",
    signInToYourMerchantAccount: "เข้าสู่ระบบบัญชีร้านค้าของคุณ",
    paymentGatewayAdminPanel: "แผงควบคุมผู้ดูแลระบบ Payment Gateway",
    accessRestrictedToAuthorizedOnly: "การเข้าถึงจำกัดเฉพาะบุคคลที่ได้รับอนุญาตเท่านั้น",
    staffMemberClickHereToLogin: "สมาชิกพนักงาน? คลิกที่นี่เพื่อเข้าสู่ระบบ",
    enginePortalAccess: "การเข้าถึงพอร์ทัล Engine",
    partnerPortalAccess: "การเข้าถึงพอร์ทัลพันธมิตร",
    merchantPortal: "พอร์ทัลร้านค้า",
    staffMemberLogin: "เข้าสู่ระบบสมาชิกพนักงาน",
    staffAccess: "การเข้าถึงพนักงาน",
    signInWithStaffCredentials: "เข้าสู่ระบบด้วยข้อมูลประจำตัวพนักงาน",
    panelType: "ประเภทแผงควบคุม",
    selectYourPanelType: "เลือกประเภทแผงควบคุมของคุณ",
    selectPanelYouWereAssignedTo: "เลือกแผงควบคุมที่คุณได้รับมอบหมาย",
    adminPanel: "แผงควบคุมผู้ดูแลระบบ",
    administrativeStaff: "พนักงานฝ่ายบริหาร",
    engineStaff: "พนักงาน Engine",
    resellerStaff: "พนักงานตัวแทนจำหน่าย",
    merchantStaff: "พนักงานร้านค้า",
    staffAccessOnly: "การเข้าถึงพนักงานเท่านั้น ไม่ใช่สมาชิกพนักงาน?",
    adminLogin: "เข้าสู่ระบบผู้ดูแลระบบ",
    engineLogin: "เข้าสู่ระบบ Engine",
    resellerLogin: "เข้าสู่ระบบตัวแทนจำหน่าย",
    merchantLogin: "เข้าสู่ระบบร้านค้า",
    iAmStaffMember: "ฉันเป็นสมาชิกพนักงาน",
    needHelpContact: "ต้องการความช่วยเหลือ? ติดต่อ",
    needHelpAccessingAccount: "ต้องการความช่วยเหลือในการเข้าถึงบัญชีของคุณ?",
    contactSupport: "ติดต่อฝ่ายสนับสนุน",
    merchantBusinessAccess: "การเข้าถึงธุรกิจร้านค้า",
    resellerPartnerAccess: "การเข้าถึงพันธมิตรตัวแทนจำหน่าย",
    allRightsReserved: "สงวนลิขสิทธิ์",
    privacyPolicy: "นโยบายความเป็นส่วนตัว",
    termsOfService: "เงื่อนไขการให้บริการ",
  }
};

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("language") || "en";
    }
    return "en";
  });

  useEffect(() => {
    localStorage.setItem("language", language);
  }, [language]);

  const t = (key) => {
    return translations[language]?.[key] || translations.en[key] || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === "en" ? "th" : "en");
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, toggleLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
}

export default LanguageContext;